import React, { useState, useEffect, useRef } from "react";
import "./searchFields.css";
import NoLenderModal from "./components/noLenderModal";
import { InputNumber, Select, Checkbox, Tooltip, Modal, message, Button, Input, Segmented, ConfigProvider } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import Fold from "./fold";
import * as com from "../../../../Common.js";
import * as common from "./../common/common.js";
import LodestarTipsCard from "../../../../Management/estimatedclosingfee/components/LodestarTipsCard";
import ClosingCost from "../../../../Management/estimatedclosingfee/components/ClosingCost";
import Lenders from "../../../../Management/Lenders";
import { checkAccess } from "Auth";
import { UR_Owner } from "../../../../State.js";
import {
  BORROWER_DEFAULTS,
  CREDIT_SCORE_MAX,
  CREDIT_SCORE_MIN,
  DEFAULT_TARGET_PRICE,
  FIRST_LIEN_PURCHASE_DEFAULTS,
  HELOC_LOAN_TERMS,
  LOAN_PROGRAM_OPTIONS_BY_LIEN,
  LOAN_PURPOSE_OPTIONS,
  PPE_FEATURES,
  PROGRAM_FIELD_DEFAULTS,
} from "../../config";
import {
  parseSearchFieldsFromUrlQuery,
  matchCountyInLoanLimits,
} from "./queryParams";
import { ITIN_CITIZENSHIP, TARGET_BASIS_COST, TARGET_BASIS_PRICE, searchTargetOf, selectedLoanTermsOf, targetPriceBasisOf } from "../../pricingRequest";
import { TargetBasisHint, TargetBasisPicker, targetPriceTip } from "../common/targetBasis";
import {
  calculateFirstLienAmounts,
  calculateLoanMetrics,
  getCollateralValue,
  getEffectiveAppraisedValue,
  resolveLoanMetricOverrides,
} from "../../loanMetrics";
import AdvancedLoanFields from "./advancedLoanFields";
import { validatePpeSearch } from "./validation";
// PPE: ARM terms are fixed period + adjustment cadence (3/1 ... 10/6); older
// fixed-period-only saved searches restore as the N/6 product.
import {
  ARM_TERM_OPTIONS,
  ARM_TERM_YEARS,
  restoreArmTerm,
} from "../../armTerms";
import {
  describeLocation,
  locationForZip,
  lookupZipCounties,
  zip5,
  zipCountiesInState,
  zipMatchesLocation,
} from "../../zipCounty";

// Names match the results ("30 Year Fixed", resultModel termLabel).
const FIXED_TERM_OPTIONS = [40, 30, 25, 20, 15, 10].map((years) => ({
  value: `fixed${years}`,
  label: `${years} Year Fixed`,
}));
const HELOC_TERM_OPTIONS = FIXED_TERM_OPTIONS.filter(({ value }) => HELOC_LOAN_TERMS.includes(value));
// Loan terms: one multi-select box grouped Fixed / ARM. Several can be
// ticked; the results price each ticked term with its own request, as adding a
// term in the results bar does. Default: 30-year fixed.
const DEFAULT_LOAN_TERMS = ["fixed30"];
const FORM_THEME = {
  token: { controlHeight: 32, borderRadius: 6, fontSize: 14 },
  components: { Segmented: { itemSelectedColor: "#222", trackPadding: 2 } },
};

// Property type labels in one case style (audit form item 15).
const PROPERTY_TYPE_OPTIONS = [
  { value: "singlefamilydetached", label: "Single Family Home, Detached" },
  { value: "singlefamilyattached", label: "Single Family Home, Attached" },
  { value: "condominiumdetached", label: "Condominium, Detached" },
  { value: "condominium", label: "Condominium, Attached" },
  // master has no condotel property type (dev only).
  { value: "nonwarrantablecondo", label: "Condominium, Non-Warrantable" },
  { value: "pudattached", label: "Planned Unit Development, Attached" },
  { value: "puddetached", label: "Planned Unit Development, Detached" },
  { value: "twotofourfamily", label: "2 to 4 Unit Multifamily" },
  { value: "manufacturedhome", label: "Manufactured Home" },
];

// State is a box you can type in: "cal" or "CA" finds California.
const STATE_OPTIONS = [
  ["AL", "ALABAMA"], ["AK", "ALASKA"], ["AZ", "ARIZONA"], ["AR", "ARKANSAS"], ["CA", "CALIFORNIA"],
  ["CO", "COLORADO"], ["CT", "CONNECTICUT"], ["DE", "DELAWARE"], ["FL", "FLORIDA"], ["GA", "GEORGIA"],
  ["HI", "HAWAII"], ["ID", "IDAHO"], ["IL", "ILLINOIS"], ["IN", "INDIANA"], ["IA", "IOWA"],
  ["KS", "KANSAS"], ["KY", "KENTUCKY"], ["LA", "LOUISIANA"], ["ME", "MAINE"], ["MD", "MARYLAND"],
  ["MA", "MASSACHUSETTS"], ["MI", "MICHIGAN"], ["MN", "MINNESOTA"], ["MS", "MISSISSIPPI"], ["MO", "MISSOURI"],
  ["MT", "MONTANA"], ["NE", "NEBRASKA"], ["NV", "NEVADA"], ["NH", "NEW HAMPSHIRE"], ["NJ", "NEW JERSEY"],
  ["NM", "NEW MEXICO"], ["NY", "NEW YORK"], ["NC", "NORTH CAROLINA"], ["ND", "NORTH DAKOTA"], ["OH", "OHIO"],
  ["OK", "OKLAHOMA"], ["OR", "OREGON"], ["PA", "PENNSYLVANIA"], ["RI", "RHODE ISLAND"], ["SC", "SOUTH CAROLINA"],
  ["SD", "SOUTH DAKOTA"], ["TN", "TENNESSEE"], ["TX", "TEXAS"], ["UT", "UTAH"], ["VT", "VERMONT"],
  ["VA", "VIRGINIA"], ["WA", "WASHINGTON"], ["WV", "WEST VIRGINIA"], ["WI", "WISCONSIN"], ["WY", "WYOMING"],
].map(([abbr, value]) => ({
  value,
  abbr,
  label: value.toLowerCase().replace(/\b[a-z]/g, (c) => c.toUpperCase()),
}));
const matchStateOption = (input, option) => {
  const typed = String(input || "").trim().toLowerCase();
  if (!typed) return true;
  return option.abbr.toLowerCase() === typed || option.label.toLowerCase().includes(typed);
};
const matchCountyOption = (input, option) => (
  String(option?.children ?? option?.label ?? "").toLowerCase().includes(String(input || "").trim().toLowerCase())
);
const formatPercent = (value) => (
  value === null || value === undefined || value === "" || !Number.isFinite(Number(value))
    ? "—"
    : `${Number(value).toFixed(2).replace(/\.?0+$/, "")}%`
);

// Errors that sit in their field's name row, and the control to focus.
const INLINE_ERROR_FIELDS = {
  fico: "ppe-fico",
  targetPrice: "ppe-target-price",
  targetRate: "ppe-target-rate",
  cashOutAmount: "ppe-cash-out",
  lineAmount: "ppe-line-amount",
  drawAmount: "ppe-draw",
  propertyValue: "ppe-property-value",
  downPayment: "ppe-property-value",
};
const isArmTerm = (term) => String(term || "").startsWith("arm");

// Citizenship is one choice; ITIN is one of them and only Non-QM prices it
// (plan D28). The calculator applies ITIN on top of non-permanent resident.
const CITIZENSHIP_OPTIONS = [
  { value: "US_CITIZENS", label: "US Citizen" },
  { value: "PERMANENT_RESIDENT_ALIEN", label: "Permanent Resident Alien" },
  { value: "NON_PERMANENT_RESIDENT_ALIEN", label: "Non-Permanent Resident Alien" },
  { value: ITIN_CITIZENSHIP, label: "ITIN" },
  { value: "NON_RESIDENT_ALIEN", label: "Non-Resident Alien (Foreign National)" },
];
// No FICO is offered for a foreign national only (plan D11): the calculator
// takes a search without a credit score for that citizenship alone.
const FOREIGN_NATIONAL_CITIZENSHIP = "NON_RESIDENT_ALIEN";
const AGENCY_PROGRAMS = ["conventional", "fha", "va", "usda"];
const DSCR_DOCUMENTATION = "DebtServiceCoverageRatio";

// PPE: heloc/secondMortgage are second-lien-only products. Lien position filters
// the available programs, while selecting either second-lien program also moves
// the form to the matching lien position.
const SECOND_LIEN_PROGRAMS = ["heloc", "secondMortgage"];
const isSecondLienProgram = (p) => SECOND_LIEN_PROGRAMS.includes(p);
const INCOME_DOCUMENTATION_PROGRAMS = ["nonqm", "heloc", "secondMortgage"];
// Income documentation, grouped by kind (pricing engine page, 11-03). The
// values are what the calculator takes and what saved searches, links and
// ppe_seed.py hold, so they never change; only the names do. An option's
// `label` is its name inside its group; the closed box and the "Income doc: ..."
// suggestions use the whole name (incomeDocumentationName), which adds the
// group where the option's own name does not say it ("Bank statements ·
// Personal · 12 months").
//
// aaaOnly: only AAA Lending's programs take it, so the list shows it only when
// AAA is among the customer's connected lenders (incomeDocumentationOptions).
//
// "PL" (a borrower-prepared P&L that does not say how many months) stays its
// own option: the dev calculator prices it differently from PL12 (11-03: on
// the same searches PL12 had eligible products at Everstream that PL had not,
// and AD Mortgage, Ameris, Bahamas, Glory, Greenbox, Panther, Willow and
// others found no product for PL at all), so it cannot be folded into PL12.
export const INCOME_DOCUMENTATION_GROUPS = [
  {
    label: "Full Doc",
    options: [
      { value: "FullDoc", label: "Full Doc" },
      { value: "FullDoc12Mo", label: "Full Doc · 12 months" },
      { value: "FullDocWithAssets", label: "Full Doc with assets" },
    ],
  },
  {
    label: "Bank statements",
    namedWithGroup: true,
    options: [
      { value: "PersonalBankStatement12Mo", label: "Personal · 12 months" },
      { value: "PersonalBankStatement24Mo", label: "Personal · 24 months" },
      { value: "BusinessBankStatement12Mo", label: "Business · 12 months" },
      { value: "BusinessBankStatement24Mo", label: "Business · 24 months" },
      { value: "BankStatement3Mo", label: "Personal or business · 3 months", aaaOnly: true },
      { value: "BusinessBankStatement6Mo", label: "Business · 6 months", aaaOnly: true },
    ],
  },
  {
    // A P&L prepared by a CPA is its own type (CPAPL); lenders that do not
    // tell them apart price it as a 12-month P&L.
    label: "P&L",
    options: [
      { value: "CPAPL", label: "CPA-prepared P&L" },
      { value: "PL12", label: "Borrower-prepared P&L · 12 months" },
      { value: "PL24", label: "Borrower-prepared P&L · 24 months" },
      { value: "PL", label: "Borrower-prepared P&L (months not stated)" },
    ],
  },
  {
    label: "1099",
    options: [
      { value: "Form109912Mo", label: "1099 · 12 months" },
      { value: "Form109924Mo", label: "1099 · 24 months" },
    ],
  },
  {
    label: "WVOE",
    options: [
      { value: "VOE", label: "WVOE" },
      { value: "VOE24Mo", label: "WVOE · 24 months" },
    ],
  },
  {
    label: "Assets",
    options: [
      { value: "AssetDepletion", label: "Asset Depletion" },
      { value: "AssetUtilization", label: "Asset Utilization" },
      { value: "AssetQualifier", label: "Asset Qualifier" },
      { value: "AssetBasedIncome", label: "Asset Based Income" },
    ],
  },
  {
    label: "DSCR",
    options: [{ value: "DebtServiceCoverageRatio", label: "DSCR" }],
  },
];
// Every option in list order, with its group and whole name.
export const INCOME_DOCUMENTATION_OPTIONS = INCOME_DOCUMENTATION_GROUPS.flatMap((group) => (
  group.options.map((option) => ({
    ...option,
    group: group.label,
    name: group.namedWithGroup ? `${group.label} · ${option.label}` : option.label,
  }))
));
// The whole name of an income documentation value; an unknown value as is.
export const incomeDocumentationName = (value) => {
  const option = INCOME_DOCUMENTATION_OPTIONS.find((o) => o.value === value);
  return option ? option.name : value;
};
// The grouped list for the Income documentation select. AAA-only options show
// when AAA is connected, or when the connected lenders are not known yet
// (aaaConnected null: the lender list has not loaded or could not be read), or
// when it is the current value (a restored search or a suggestion), so the
// box never holds a value the list does not show. Empty groups are left out.
export const incomeDocumentationOptions = ({ aaaConnected = null, current, groups = INCOME_DOCUMENTATION_GROUPS } = {}) => (
  groups
    .map((group) => ({
      label: group.label,
      options: group.options
        .filter((option) => !option.aaaOnly || aaaConnected !== false || option.value === current)
        .map(({ value, label }) => ({ value, label })),
    }))
    .filter((group) => group.options.length > 0)
);
// The customer's lender list (/data/getcustomerlenders) says whether AAA is
// connected: a row for lender "AAA" that is Active, which is how the
// calculator builds connected_lender_map (customer_lenders_data, active).
export const isAaaConnected = (lenders) => (
  (lenders || []).some((lender) => lender && lender.lender === "AAA" && lender.Active)
);
// PPE: a condominium that does not meet agency warrantability. Sent as the
// attached-condo propertyType plus nonWarrantableCondo: true.
const NON_WARRANTABLE_CONDO_PROPERTY_TYPE = "nonwarrantablecondo";
// PPE: the only multi-unit property type; drives the Units default.
const MULTIFAMILY_PROPERTY_TYPE = "twotofourfamily";
const SEASONING_BUCKETS = new Set([
  "lt12",
  "12-23",
  "24-35",
  "36-47",
  "48-59",
  "60-83",
  "gte84",
]);
const DEFAULT_SEASONING_BUCKET = "lt12";
const BANKRUPTCY_OUTCOMES = new Set(["discharged", "dismissed", "openPending"]);
const DEFAULT_BANKRUPTCY_OUTCOME = "discharged";

// A field's name, as a <label> tied to its control so screen readers name the
// control and clicking the name focuses it (audit 2026-11-02, item 2).
const FieldTitle = ({ htmlFor, children }) => (
  <label htmlFor={htmlFor} className="search-fields-content-item-content-item-title">
    {children}
  </label>
);

// "?" next to a field name. Focusable, so the explanation is reachable from
// the keyboard too.
const Tip = ({ title, label }) => (
  <Tooltip title={title}>
    <span className="ppe-tip" tabIndex={0} role="img" aria-label={label || "More information"}>
      <QuestionCircleOutlined />
    </span>
  </Tooltip>
);

// One field (Option B, 11-03): a 16px name row over one 32px control, so
// every field is 58px tall with the gap and the four columns line up row by
// row. The name row also holds a "?" tip, an action link (Edit fees) or the
// field's error, at its right end, so an error never moves the fields below.
// `group`: the control is a set of buttons (Segmented, term buttons), which a
// <label> cannot name; the name is then a plain element the group points to
// with aria-labelledby (`${id}-label`).
const Field = ({ id, title, tip, tipLabel, end, error, group, className = "", children }) => (
  <div className={`search-fields-content-item-content-item ppe-field ${className}`}>
    <div className="ppe-label-row">
      {group ? (
        <span id={`${id}-label`} className="search-fields-content-item-content-item-title">{title}</span>
      ) : (
        <FieldTitle htmlFor={id}>{title}</FieldTitle>
      )}
      {tip && <Tip title={tip} label={tipLabel} />}
      {error ? (
        <span className="ppe-field-error" id={`${id}-error`} role="alert" title={error}>{error}</span>
      ) : (end && <div className="ppe-label-end">{end}</div>)}
    </div>
    {children}
  </div>
);

// A choice among a few short options, all visible (audit form item 10): one
// click instead of opening a list. antd Segmented is a radio group, so arrow
// keys move the choice.
const Choice = ({ id, value, onChange, options, className = "" }) => (
  <Segmented
    block
    id={id}
    className={`ppe-seg ${className}`}
    aria-label={undefined}
    aria-labelledby={`${id}-label`}
    value={value}
    onChange={onChange}
    options={options}
  />
);

// A button that stays pressed (No Ratio next to DSCR).
const ToggleButton = ({ on, title, onClick, children, className = "" }) => (
  <button
    type="button"
    className={`ppe-chip ${on ? "is-on" : ""} ${className}`}
    aria-pressed={on}
    title={title}
    onClick={onClick}
  >
    {children}
  </button>
);

// A value the form works out and the loan officer cannot type (Existing DTI,
// Estimated closing fee, fixed income documentation): text in the control's
// row, not a grey box that looks like an input (audit form item 11).
const ReadOnlyValue = ({ id, children, muted }) => (
  <div id={id} className={`ppe-readonly-value ${muted ? "is-muted" : ""}`} aria-labelledby={`${id}-label`} role="status">
    {children}
  </div>
);

function normalizeSeasoningBucket(value) {
  return SEASONING_BUCKETS.has(value) ? value : "";
}

function normalizeBankruptcyOutcome(value) {
  return BANKRUPTCY_OUTCOMES.has(value) ? value : "";
}

function hasMortgageLateCounts(mortgageLates) {
  if (!mortgageLates) return false;
  const last12mo = mortgageLates.last12mo || {};
  const m13to24 = mortgageLates.m13to24 || {};
  return [last12mo, m13to24].some((bucket) =>
    [bucket.x30, bucket.x60, bucket.x90, bucket.x120].some((count) => Number(count) > 0)
  );
}

/** 深链模式：仅预填 URL 中出现的字段，其余留空；普通进入：沿用表单原默认值 */
function pickInitial(deepLink, field, legacyDefault, emptyValue = null) {
  if (!deepLink?.fromDeepLink) return legacyDefault;
  return Object.prototype.hasOwnProperty.call(deepLink, field)
    ? deepLink[field]
    : emptyValue;
}

function calculateDti(monthlyIncome, monthlyDebt) {
  if (
    monthlyIncome === null || monthlyIncome === undefined || monthlyIncome === "" ||
    monthlyDebt === null || monthlyDebt === undefined || monthlyDebt === ""
  ) {
    return null;
  }
  const income = Number(monthlyIncome);
  const debt = Number(monthlyDebt);
  if (!Number.isFinite(income) || income <= 0 || !Number.isFinite(debt) || debt < 0) {
    return null;
  }
  return Number(((debt / income) * 100).toFixed(2));
}

const SearchFields = ({ onCheckRates, historyParams , onBackToResult}) => {
  const [deepLinkInitial] = useState(() => parseSearchFieldsFromUrlQuery());
  const fromDeepLink = deepLinkInitial?.fromDeepLink === true;
  const queryCountySyncedRef = useRef(false);
  const [showEditCostDetails, setShowEditCostDetails] = useState(false);
  const [lodeStarConfig, setLodeStarConfig] = useState({
    suggestEnableCount: 1,
    isModified: false,
    enableOriginationCharges: false,
    enableCanNotShopServices: false,
    enableCanShopServices: false,
    enableTaxesAndOtherGovernmentFees: false,
    enablePrepaids: false,
    enableInitialEscrowPaymentsAtClosing: false,
    enableOther: false,
  });
  const [closingFeeConfig, setClosingFeeConfig] = useState({
    originationCharges: null,
    canNotShopServices: null,
    canShopServices: null,
    taxesAndOtherGovernmentFees: null,
    prepaids: null,
    initialEscrowPaymentsAtClosing: null,
    other: null,
  });
  const [totalClosingFee, setTotalClosingFee] = useState(0);
  const closingCostRef = useRef();

  const [isOpen_no_lender, setIsOpen_no_lender] = useState(false);
  const [isCalculatedAtResults, setIsCalculatedAtResults] = useState(false);
  const [hasLenders, setHasLenders] = useState(false);
  // Whether AAA is among the connected lenders: null until the lender list
  // has loaded (or if it cannot be read), then true or false. AAA-only income
  // documentation shows unless this is false.
  const [aaaConnected, setAaaConnected] = useState(null);
  const [shouldCheckRatesAfterLenderUpdate, setShouldCheckRatesAfterLenderUpdate] = useState(false);
  // const [loading, setLoading] = useState(false);
  // const [connectedLendersMap, setConnectedLendersMap] = useState({});
  // const [resultlenderSet, setResultlenderSet] = useState(new Set());
  // const [lenderData, setLenderData] = useState([]);
  // const [selectedLenders, setSelectedLenders] = useState([]);
  // const [showConnectedLendersOnly, setShowConnectedLendersOnly] =
  //   useState(false);
  // const [showIneligible, setShowIneligible] = useState(false);
  // const [showAddLendersModal, setShowAddLendersModal] = useState(false);
  // const [ineligibleProducts, setIneligibleProducts] = useState({});
  // const [noProducts, setNoProducts] = useState(false);
  // const [productTypes, setProductTypes] = useState([]);
  // const [loans, setLoans] = useState([]);
  // const [indexFixed30, setIndexFixed30] = useState(0);
  // const [indexFixed25, setIndexFixed25] = useState(0);
  // const [indexFixed20, setIndexFixed20] = useState(0);
  // const [indexFixed15, setIndexFixed15] = useState(0);
  // const [indexFixed10, setIndexFixed10] = useState(0);
  // const [indexArm56, setIndexArm56] = useState(0);
  // const [indexArm76, setIndexArm76] = useState(0);
  // const [indexArm106, setIndexArm106] = useState(0);
  // const [selectedAUS, setSelectedAUS] = useState([]);
  // const [allRates, setAllRates] = useState({});
  const [targetPriceMessage, setTargetPriceMessage] = useState('');
  const [from, setFrom] = useState("search");
  const [isLoading, setIsLoading] = useState(false);
  // product
  // The target is a price (the Price column: lock-day price plus adjustments,
  // compensation not included, 11-04) or a rate (back on 11-04). No
  // component picker.
  const [searchBy, setSearchBy] = useState("price");
  const [targetPrice, setTargetPrice] = useState(DEFAULT_TARGET_PRICE);
  const [targetRate, setTargetRate] = useState(null);
  // What the target price is matched against (11-04): the Price column or
  // the Cost to borrower as a price. The box takes the same number.
  const [targetBasis, setTargetBasis] = useState(TARGET_BASIS_PRICE);
  const [lockDay, setLockDay] = useState(30);
  const [compensationType, setCompensationType] = useState("lender_paid");
  const [compensationPercent, setCompensationPercent] = useState(0);
  const [estimatedClosingFee, setEstimatedClosingFee] = useState(0);
  // keep this an array, never undefined: antd/rc-util treats a `undefined`
  // value as "uncontrolled" and falls back to the Select's own internal state,
  // so the tags would survive a reset while ausType no longer has a .length.
  const [ausType, setAusType] = useState([]);

  // product

  // product
  const updateTargetPrice = (value) => {
    if(value === null){
      setTargetPriceMessage('Please enter target price');
    }else if(value < 80 || value > 120){
      setTargetPriceMessage('Target price must be between 80 and 120');
    }else{
      setTargetPriceMessage('');
    }
    setTargetPrice(value);
  };

  const updateTargetRate = (value) => {
    if (value === null || value === undefined) {
      setTargetPriceMessage('Please enter target rate');
    } else if (value <= 0 || value >= 20) {
      setTargetPriceMessage('Target rate must be between 0% and 20%');
    } else {
      setTargetPriceMessage('');
    }
    setTargetRate(value);
  };

  const updateSearchBy = (value) => {
    setSearchBy(value);
    setTargetPriceMessage('');
  };

  const updateLockDay = (value) => {
    // antd Select returns the option value as a string ("60"); keep lockDay
    // numeric so the strict lockDay comparison in the result table matches.
    setLockDay(Number(value) || 30);
  };

  const updateCompensationType = (value) => {
    setCompensationType(value);
  };

  const updateCompensationPercent = (value) => {
    setCompensationPercent(value);
  };


  // product

  // borrower
  // No FICO, income or debt by default (plan D2, D3): a blank income means
  // the borrower is treated as qualifying on income, and the FICO must be
  // the loan officer's, not a default of the best tier.
  const [fico, setFico] = useState(pickInitial(deepLinkInitial, "fico", null, null));
  const [noFico, setNoFico] = useState(false);
  const initialMonthlyIncome = pickInitial(deepLinkInitial, "monthlyIncome", null, null);
  const initialMonthlyDebt = pickInitial(deepLinkInitial, "monthlyDebt", null, null);
  const [monthlyIncome, setMonthlyIncome] = useState(initialMonthlyIncome);
  const [monthlyDebt, setMonthlyDebt] = useState(initialMonthlyDebt);
  const [dti, setDti] = useState(() => calculateDti(initialMonthlyIncome, initialMonthlyDebt));
  const [citizenship, setCitizenship] = useState(
    pickInitial(deepLinkInitial, "citizenship", "US_CITIZENS", "US_CITIZENS"),
  );
  // Unticked by default (plan ppe-simplify-epps D2): ticked, it silently
  // dropped every product that does not take first-time home buyers.
  const [isFirstTimeHomeBuyer, setIsFirstTimeHomeBuyer] = useState(false);
  const [isSelfEmployed, setIsSelfEmployed] = useState(false);
  const [isImpound, setIsImpound] = useState(true); // "Yes"
  const [isInterestOnly, setIsInterestOnly] = useState(false);
  const [isWaiveLenderFee, setIsWaiveLenderFee] = useState(false);
  const [ficoError, setFicoError] = useState("");

  // property
  const [occupancy, setOccupancy] = useState(
    pickInitial(deepLinkInitial, "occupancy", "principal", undefined),
  );
  const [propertyType, setPropertyType] = useState("singlefamilydetached");
  const [state, setState] = useState(
    pickInitial(deepLinkInitial, "state", "CALIFORNIA", undefined),
  );
  const [county, setCounty] = useState(
    pickInitial(deepLinkInitial, "county", "ALAMEDA COUNTY", undefined),
  );

  // borrower

  const updateFico = (value) => {
    if (value !== null && value !== undefined && value !== ""
      && (value < CREDIT_SCORE_MIN || value > CREDIT_SCORE_MAX)) {
      setFicoError(`FICO must be ${CREDIT_SCORE_MIN} to ${CREDIT_SCORE_MAX}.`);
    } else {
      setFicoError("");
    }
    setFico(value === null || value === undefined || value === "" ? null : Number(value));
  };

  const updateNoFico = (e) => {
    setNoFico(e.target.checked);
    setFicoError("");
  };

  const updateMonthlyIncome = (value) => {
    setMonthlyIncome(value);
    setDti(calculateDti(value, monthlyDebt));
  };

  const updateMonthlyDebt = (value) => {
    setMonthlyDebt(value);
    setDti(calculateDti(monthlyIncome, value));
  };

  const updateCitizenship = (value) => {
    setCitizenship(value);
    if (value !== FOREIGN_NATIONAL_CITIZENSHIP) setNoFico(false);
  };

  const updateFirstTimeHomeBuyer = (e) => {
    setIsFirstTimeHomeBuyer(e.target.checked);
  };

  const updateSelfEmployed = (e) => {
    setIsSelfEmployed(e.target.checked);
  };

  const updateImpound = (e) => {
    setIsImpound(e.target.checked);
  };

  const updateInterestOnly = (e) => {
    setIsInterestOnly(e.target.checked);
  };

  const updateWaiveLenderFee = (e) => {
    setIsWaiveLenderFee(e.target.checked);
  };

  const updateOccupancy = (value) => {
    setOccupancy(value);
  };

  const updatePropertyType = (value) => {
    setPropertyType(value);
    // 2-4 unit multifamily starts at 2 units; every other type is single-unit.
    setPpeUnits(value === MULTIFAMILY_PROPERTY_TYPE ? 2 : 1);
  };

  // State or County picked by hand: a ZIP outside the new county is cleared,
  // so the request never carries a ZIP, state and county that disagree.
  const clearZipOutside = (nextState, nextCounty) => {
    const counties = currentZipCounties();
    if (!zipMatchesLocation(counties, nextState, nextCounty)) {
      setZipNote(`Cleared ZIP ${zip5(ppeZip)}: it is not in ${describeLocation(nextState, nextCounty)}.`);
      setPpeZip("");
    }
  };

  const updateState = (value) => {
    setState(value);
    // 当state变化时，自动选择第一个可用的county
    // (the ZIP's own county in that state when there is one)
    let nextCounty = "";
    if (value && value in loanLimits) {
      const countyKeys = Object.keys(loanLimits[value]);
      nextCounty = zipCountiesInState(currentZipCounties(), value, loanLimits)[0] || countyKeys[0] || "";
    }
    setCounty(nextCounty);
    clearZipOutside(value, nextCounty);
  };

  const updateCounty = (value) => {
    setCounty(value);
    clearZipOutside(state, value);
  };

  // borrower

  // loan
  const [loanPurpose, setLoanPurpose] = useState(
    pickInitial(deepLinkInitial, "purpose", "Purchase", undefined),
  );
  // The loan terms to price, in the order picked. The results price each one
  // with its own request; the body's loanTerm is the first.
  const [selectedLoanTerms, setSelectedLoanTerms] = useState(() => {
    const linked = pickInitial(deepLinkInitial, "loanTerms", DEFAULT_LOAN_TERMS, DEFAULT_LOAN_TERMS);
    return selectedLoanTermsOf({ selectedLoanTerms: linked }, deepLinkInitial?.loanProgram);
  });
  const loanTerm = selectedLoanTerms[0];
  const [loanType, setLoanType] = useState(["conventional"]);
  // PPE: explicit merged program selector (drives QM/NonQM routing on the new endpoint)
  const [loanProgram, setLoanProgram] = useState(
    pickInitial(deepLinkInitial, "loanProgram", "conventional", "conventional"),
  );
  // Keep the full Non-QM details state independent from the compact second-lien
  // documentation controls so changing one flow cannot alter the other.
  const [nqIncomeDoc, setNqIncomeDoc] = useState("FullDoc");
  const [nqDscrMode, setNqDscrMode] = useState("number");
  const [nqDscr, setNqDscr] = useState(1.2);
  const [secondIncomeDoc, setSecondIncomeDoc] = useState("FullDoc");
  const [secondDscrMode, setSecondDscrMode] = useState("number");
  const [secondDscr, setSecondDscr] = useState(1.2);
  const [nqHousingEvent, setNqHousingEvent] = useState("");
  const [nqHousingSeasoning, setNqHousingSeasoning] = useState("");
  const [nqBkType, setNqBkType] = useState("");
  const [nqBkOutcome, setNqBkOutcome] = useState("");
  const [nqBkSeasoning, setNqBkSeasoning] = useState("");
  const [nqHasMortgageLates, setNqHasMortgageLates] = useState(false);
  const [nqLates, setNqLates] = useState({ l12_30: 0, l12_60: 0, l12_90: 0, l12_120: 0, l24_30: 0, l24_60: 0, l24_90: 0, l24_120: 0 });
  const [nqShortTermRental, setNqShortTermRental] = useState(false);
  // Individual or entity (LLC) vesting; the choice shows once work package 4
  // ships (PPE_FEATURES.entityVesting).
  const [vesting, setVesting] = useState("individual");
  const setLate = (k) => (v) => setNqLates((p) => ({ ...p, [k]: Number(v) || 0 }));
  // PPE: additional fields carried by the unified pricing request.
  const [ppeLien, setPpeLien] = useState(isSecondLienProgram(loanProgram) ? "second" : "first");
  const [ppeAppraised, setPpeAppraised] = useState(null);
  // First-lien appraisal is an Advanced-section override. A collapsed
  // Advanced section must use the visible Home value for pricing.
  const [ppeAppraisalOverrideActive, setPpeAppraisalOverrideActive] = useState(false);
  const [ppeTotalLoan, setPpeTotalLoan] = useState(null);
  const [ppeCashOut, setPpeCashOut] = useState(null);
  const [ppeCashOutError, setPpeCashOutError] = useState("");
  const [ppeCltv, setPpeCltv] = useState(null);
  const [ppeHcltv, setPpeHcltv] = useState(null);
  // The default second-lien scenario is 50k behind a 250k first mortgage on the
  // form's 500k property baseline, producing a 60% CLTV out of the box. The line
  // amount also satisfies the engine's required HELOC field.
  const [ppeLine, setPpeLine] = useState(PROGRAM_FIELD_DEFAULTS.helocLineAmount);
  const [ppeDraw, setPpeDraw] = useState(PROGRAM_FIELD_DEFAULTS.helocDrawAmount);
  const [ppeDrawTermMonths, setPpeDrawTermMonths] = useState(PROGRAM_FIELD_DEFAULTS.helocDrawTermMonths);
  const [ppeHelocAmountError, setPpeHelocAmountError] = useState("");
  const [ppePrimaryMtg, setPpePrimaryMtg] = useState(
    PROGRAM_FIELD_DEFAULTS.secondLienPrimaryMortgageAmount,
  );
  // Percent. The engine rejects loanProgram=va without a fundingFee (unless the
  // exempt flag is set), so this cannot start empty. 2.15 = first use, <5% down.
  const [ppeFundingFee, setPpeFundingFee] = useState(PROGRAM_FIELD_DEFAULTS.vaFundingFee);
  const [ppeVaSubseq, setPpeVaSubseq] = useState(false);
  const [ppeVaExempt, setPpeVaExempt] = useState(false);
  // Percent. Same story as fundingFee: loanProgram=usda is rejected without it.
  const [ppeGuaranteeFee, setPpeGuaranteeFee] = useState(PROGRAM_FIELD_DEFAULTS.usdaGuaranteeFee);
  const [ppeReserves, setPpeReserves] = useState(
    pickInitial(deepLinkInitial, "reserves", BORROWER_DEFAULTS.reserves, BORROWER_DEFAULTS.reserves),
  );
  const [ppePropsFinanced, setPpePropsFinanced] = useState(
    pickInitial(
      deepLinkInitial,
      "propertiesFinanced",
      BORROWER_DEFAULTS.propertiesFinanced,
      BORROWER_DEFAULTS.propertiesFinanced,
    ),
  );
  const [ppeUnits, setPpeUnits] = useState(1);
  const [ppeZip, setPpeZip] = useState(pickInitial(deepLinkInitial, "zip", "", ""));
  const deepLinkZipFilledRef = useRef(false);
  // { zip, counties } for the ZIP in the field (see lookupZipCounties); only
  // read, never applied, so restoring a saved search keeps its state/county.
  const [zipLookup, setZipLookup] = useState(null);
  const [zipNote, setZipNote] = useState("");
  const latestFieldsRef = useRef({});
  const [ppePrepay, setPpePrepay] = useState(0);
  const [ppeInterestOnly, setPpeInterestOnly] = useState(false);
  const [ppeAdvOpen, setPpeAdvOpen] = useState(false);
  const [propertyValue, setPropertyValue] = useState(
    pickInitial(deepLinkInitial, "housePrice", FIRST_LIEN_PURCHASE_DEFAULTS.propertyValue, null),
  );
  const [propteryError, setPropteryError] = useState('');
  const [loanAmount, setLoanAmount] = useState(
    // legacy default: propertyValue 500000 - downPayment 100000 (LTV 80)
    pickInitial(deepLinkInitial, "loanAmount", FIRST_LIEN_PURCHASE_DEFAULTS.loanAmount, null),
  );
  // FHA UFMIP as a PERCENT of the base loan amount (engine default: 1.75).
  // Converted to a dollar amount at request-build time (result/index.js).
  const [upFrontMIP, setUpFrontMIP] = useState(1.75);
  const [upFrontMIP_Financed, setUpFrontMIP_Financed] = useState(true);
  const [downPayment, setDownPayment] = useState(() => {
    if (!deepLinkInitial?.fromDeepLink) return FIRST_LIEN_PURCHASE_DEFAULTS.downPayment;
    const { housePrice, loanAmount } = deepLinkInitial;
    if (housePrice != null && loanAmount != null) {
      return Math.max(housePrice - loanAmount, 0);
    }
    return null;
  });
  const [secondaryLoanAmount, setSecondaryLoanAmount] = useState(
    FIRST_LIEN_PURCHASE_DEFAULTS.secondaryLoanAmount,
  );
  const [isSubordinateLiens, setIsSubordinateLiens] = useState(false);
  const [lienType, setLienType] = useState();
  const [lienPosition, setLienPosition] = useState();
  const [helocLineAmount, setHelocLineAmount] = useState(0);
  const [helocDrawAmount, setHelocDrawAmount] = useState(0);
  const [helocLtv, setHelocLtv] = useState(0);

  const getEffectivePpeAppraisedValue = () => getEffectiveAppraisedValue({
    propertyValue,
    appraisedValue: ppeAppraised,
    useAppraisal: isSecondLienProgram(loanProgram) || ppeAppraisalOverrideActive,
  });

  const getDerivedFirstLienAmounts = () => calculateFirstLienAmounts({
    loanPurpose,
    propertyValue,
    downPayment,
    loanAmount,
    secondaryLoanAmount,
    totalLoanAmount: ppeAdvOpen ? ppeTotalLoan : null,
  });

  // The subject loan amount a cost-to-borrower target is a percent of (the
  // calculator's baseLoanAmount): a HELOC's draw, or its line when nothing
  // is drawn; a second mortgage's amount; the first lien's base amount.
  const formSubjectLoanAmount = () => {
    if (loanProgram === "heloc") return Number(ppeDraw) > 0 ? Number(ppeDraw) : Number(ppeLine) || 0;
    if (isSecondLienProgram(loanProgram)) return Number(loanAmount) || 0;
    return Number(getDerivedFirstLienAmounts().baseLoanAmount) || 0;
  };

  // loan

  // Keep lien position in sync with the program so no invalid pair can reach
  // the pricing endpoint.
  const updateLoanProgram = (value) => {
    setLoanProgram(value);
    setPpeLien(isSecondLienProgram(value) ? "second" : "first");
    // Program switches start a fresh loan scenario. Do not carry hidden
    // Advanced overrides or subordinate financing into a different product.
    setPpeTotalLoan(null);
    setPpeCltv(null);
    setPpeHcltv(null);
    setSecondaryLoanAmount(0);
    if (!isSecondLienProgram(value)) {
      // The first-lien appraisal is an Advanced override. Keep the value for
      // possible later editing, but do not let a second-lien appraisal leak
      // into a collapsed first-lien search.
      setPpeAppraisalOverrideActive(false);
    }
    if (isSecondLienProgram(value)) {
      setSecondIncomeDoc("FullDoc");
      setSecondDscrMode("number");
    }
    if (isSecondLienProgram(loanProgram) && !isSecondLienProgram(value)) {
      // Leaving a second-lien product must not carry its subject amount into a
      // first-lien search. Restore the purpose-appropriate first-lien baseline.
      if (loanPurpose === "Purchase") {
        setPropertyValue(FIRST_LIEN_PURCHASE_DEFAULTS.propertyValue);
        setDownPayment(FIRST_LIEN_PURCHASE_DEFAULTS.downPayment);
        setLoanAmount(FIRST_LIEN_PURCHASE_DEFAULTS.loanAmount);
      } else {
        setPropertyValue(500000);
        setDownPayment(null);
        setLoanAmount(100000);
      }
    }
    // ITIN is offered only for Non-QM (plan D28). Leaving Non-QM moves it to
    // the residency the calculator prices ITIN on, and says so.
    if (value !== "nonqm" && citizenship === ITIN_CITIZENSHIP) {
      setCitizenship("NON_PERMANENT_RESIDENT_ALIEN");
      message.info("ITIN is offered only for Non-QM. Citizenship is now Non-Permanent Resident Alien.");
    }
    // Entity Vesting sits in the Non-QM section (plan D12); leaving Non-QM
    // goes back to individual vesting, so a hidden box never prices.
    if (value !== "nonqm") setVesting("individual");
    if (value === "heloc") {
      // A HELOC is priced on a 15, 25 or 30-year repayment term.
      setSelectedLoanTerms((terms) => {
        const helocTerms = terms.filter((term) => HELOC_LOAN_TERMS.includes(term));
        return helocTerms.length ? helocTerms : DEFAULT_LOAN_TERMS;
      });
      setPpeLine(PROGRAM_FIELD_DEFAULTS.helocLineAmount);
      setPpeDraw(PROGRAM_FIELD_DEFAULTS.helocDrawAmount);
      setPpeDrawTermMonths(PROGRAM_FIELD_DEFAULTS.helocDrawTermMonths);
      setPpePrimaryMtg(PROGRAM_FIELD_DEFAULTS.secondLienPrimaryMortgageAmount);
      setPpeHelocAmountError("");
    } else if (value === "secondMortgage") {
      setLoanAmount(PROGRAM_FIELD_DEFAULTS.secondMortgageAmount);
      setPpePrimaryMtg(PROGRAM_FIELD_DEFAULTS.secondLienPrimaryMortgageAmount);
    }
  };

  const updatePpeLien = (value) => {
    const nextProgram = value === "second"
      ? (isSecondLienProgram(loanProgram) ? loanProgram : "secondMortgage")
      : (isSecondLienProgram(loanProgram) ? "conventional" : loanProgram);
    setPpeLien(value);
    if (nextProgram !== loanProgram) {
      updateLoanProgram(nextProgram);
    }
  };

  const updateLoanPurpose = (value) => {
    setLoanPurpose(value);
    if(value === "Purchase"){
      setPropertyValue(FIRST_LIEN_PURCHASE_DEFAULTS.propertyValue);
      setDownPayment(FIRST_LIEN_PURCHASE_DEFAULTS.downPayment);
      setLoanAmount(
        loanProgram === "secondMortgage"
          ? PROGRAM_FIELD_DEFAULTS.secondMortgageAmount
          : FIRST_LIEN_PURCHASE_DEFAULTS.loanAmount,
      );
    } else {
      setPropertyValue(500000);
      setLoanAmount(
        loanProgram === "secondMortgage"
          ? PROGRAM_FIELD_DEFAULTS.secondMortgageAmount
          : 100000,
      );
      setDownPayment(null);
    }
    // A purpose change starts a new collateral-value scenario. Do not carry a
    // hidden appraisal override from the previous purpose.
    setPpeAppraised(null);
    setPpeAppraisalOverrideActive(false);
    setPpeCashOut(null);
    setPpeCashOutError("");
    setPpeTotalLoan(null);
    setPpeCltv(null);
    setPpeHcltv(null);
    setSecondaryLoanAmount(0);
    setPropteryError('');
  };

  const updatePpeAppraised = (value) => {
    setPpeAppraised(value);
    setPpeAppraisalOverrideActive(value !== undefined && value !== null && value !== "");
  };

  const updatePpeCashOut = (value) => {
    setPpeCashOut(value);
    if (loanPurpose === "CashOutRefi" && !(Number(value) > 0)) {
      setPpeCashOutError("Cash-Out amount must be greater than $0.");
    } else if (Number(value) > Number(loanProgram === "heloc" ? ppeDraw : loanAmount || 0)) {
      setPpeCashOutError("Cash-Out amount cannot exceed the new loan amount.");
    } else {
      setPpeCashOutError("");
    }
  };

  const toggleAdvanced = () => {
    // Opening Advanced only reveals optional overrides. It must not activate a
    // value restored from an older search; activation happens when the user
    // actually edits Appraised value. Closing Advanced disables the override
    // so hidden data can never affect pricing.
    setPpeAdvOpen((open) => {
      if (open) {
        setPpeAppraisalOverrideActive(false);
        setPpeTotalLoan(null);
        setPpeCltv(null);
        setPpeHcltv(null);
        setSecondaryLoanAmount(0);
      }
      return !open;
    });
  };

  const updateVaExemptFundingFee = (event) => {
    const exempt = event.target.checked;
    setPpeVaExempt(exempt);
    setPpeFundingFee(exempt ? 0 : PROGRAM_FIELD_DEFAULTS.vaFundingFee);
  };

  // "All" for one group of the multi-select: picks every term of the group,
  // or clears the group when all of it is already picked.
  const toggleLoanTermGroup = (options) => {
    const values = options.map(({ value }) => value);
    setSelectedLoanTerms((terms) => (
      values.every((value) => terms.includes(value))
        ? terms.filter((term) => !values.includes(term))
        : [...terms, ...values.filter((value) => !terms.includes(value))]
    ));
  };

  const loanTypeSelect = (value) => {
    // 如果选择的是 ALL
    if (value === "") {
      setLoanType([""]);
      return;
    }
    
    // 如果当前已经选择了 ALL，则移除 ALL
    if (loanType.includes("")) {
      setLoanType([value]);
      return;
    }

    // 如果选择了所有具体选项（除了ALL），自动添加ALL
    if (loanType.length === 4 && !loanType.includes(value)) {
      setLoanType([""]);
      return;
    }

    // 其他情况正常添加
    setLoanType([...loanType, value]);
  };

  const loanTypeDeselect = (value) => {
    // 如果取消选择的是 ALL
    if (value === "") {
      setLoanType([]);
      return;
    }

    // 如果取消选择后只剩下一个选项，则添加 ALL
    if (loanType.length === 1 && loanType.includes(value)) {
      setLoanType([""]);
      return;
    }

    // 其他情况正常移除
    setLoanType(loanType.filter(item => item !== value));
  };

  const updatePropertyValue = (value) => {
    setPropertyValue(value);
    if (loanPurpose === "Purchase" && !isSecondLienProgram(loanProgram)) {
      setLoanAmount(value - downPayment);
    }
    check_property_value('propertyValue', value);
  };

  const updateLoanAmount = (value) => {
    setLoanAmount(value);
    if (loanPurpose === "Purchase" && !isSecondLienProgram(loanProgram)) {
      setDownPayment(propertyValue - value);
    }
    check_property_value('loanAmount', value);
  };

  const updateDownPayment = (value) => {
    setDownPayment(value);
    setLoanAmount(propertyValue - value);
    check_property_value('downPayment', value);
  };

  const updateSecondaryLoanAmount = (value) => {
    setSecondaryLoanAmount(value);
  };

  const updateLtv = (value) => {
    const collateralValue = getCollateralValue({
      loanPurpose,
      propertyValue,
      appraisedValue: getEffectivePpeAppraisedValue(),
    });
    const nextLoanAmount = collateralValue * (Number(value) || 0) / 100;
    if (loanProgram === "heloc") {
      setPpeDraw(nextLoanAmount);
      validatePpeHelocAmounts(ppeLine, nextLoanAmount);
      return;
    }
    if (loanPurpose === "Purchase" && !isSecondLienProgram(loanProgram)) {
      setDownPayment(Number(propertyValue) - Number(nextLoanAmount.toFixed(0)));
      setLoanAmount(nextLoanAmount.toFixed(0));
      check_property_value('downPayment', Number(propertyValue) - nextLoanAmount);
    }else{
      setLoanAmount(nextLoanAmount);
      check_property_value('loanAmount', nextLoanAmount);
    }
  };

  const updateSubordinateLiens = (e) => {
    setIsSubordinateLiens(e.target.checked);
  };

  const updateLienType = (value) => {
    setLienType(value);
    if (value === "Closed_end") {
      setLienPosition("2nd");
    } else {
      setLienPosition("");
    }
  };

  const updateLienPosition = (value) => {
    setLienPosition(value);
  };

  const updateHelocLineAmount = (value) => {
    setHelocLineAmount(value);
  };

  const updateHelocDrawAmount = (value) => {
    setHelocDrawAmount(value);
  };

  const updateHelocLtv = (value) => {
    setHelocLtv(value);
  };

  // A HELOC draw is a utilization of the approved line, never a second
  // independent loan amount. Keep this constraint visible in the form
  // so an invalid request is stopped before the backend returns a 400.
  const validatePpeHelocAmounts = (line, draw) => {
    const lineAmount = Number(line);
    const drawAmount = Number(draw);
    if (Number.isFinite(lineAmount) && Number.isFinite(drawAmount) && drawAmount > lineAmount) {
      setPpeHelocAmountError("Draw amount cannot exceed the line amount.");
      return false;
    }
    setPpeHelocAmountError("");
    return true;
  };

  // property

  const [loanLimits, setLoanLimits] = useState({});

  const getloanestimatedclosingfee = () => {
    let token = sessionStorage.getItem("ZeitroA");
    fetch("/los/getloanestimatedclosingfee", {
      method: "GET",
      headers: {
        Authorization: "Bearer " + token,
        Cache: "no-cache",
      },
    })
      .then((response) => {
        if (response.status !== 200) {
          alert(
            "Looks like there was a problem. Status Code: " + response.status
          );
          return;
        }
        response.json().then((js) => {
          if (js.status !== "OK") {
          } else {
            let data = js.configurations;
            if (data.estimatedClosingCostsDetail) {
              setClosingFeeConfig(data.estimatedClosingCostsDetail);
            }
            if (data.lodeStarConfig) {
              setLodeStarConfig(data.lodeStarConfig);
            }
          }
        });
      })
      .catch(function (err) {});
  };

  const getLoanLimits = () => {
    fetch("/data/loanlimits")
      .then((res) => {
        if (!res.ok) {
          throw Error(res.statusText);
        }
        return res.json();
      })
      .then((res) => {
        setLoanLimits(res);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const getPropertyValue = (item) => {
    return (item.loanAmount / item.LTV * 100)
  }

  // useEffect(() => {
  //   setMonthlyDebt(parseInt(monthlyIncome * dti / 100));
  // }, [monthlyIncome, dti]);

  useEffect(() => {
    getLender();
    getloanestimatedclosingfee();
    getLoanLimits();
  }, []);

  useEffect(() => {
    const zip = zip5(ppeZip);
    if (!zip) {
      setZipLookup(null);
      return undefined;
    }
    let cancelled = false;
    lookupZipCounties(zip)
      .then((counties) => {
        if (!cancelled) setZipLookup({ zip, counties });
      })
      .catch((err) => {
        console.error(err);
        if (!cancelled) setZipLookup(null);
      });
    return () => {
      cancelled = true;
    };
  }, [ppeZip]);

  // A link with a ZIP and no State fills State and County from the ZIP, as
  // typing it would.
  useEffect(() => {
    if (
      !fromDeepLink ||
      deepLinkZipFilledRef.current ||
      !deepLinkInitial?.zip ||
      deepLinkInitial?.state ||
      Object.keys(loanLimits).length === 0
    ) {
      return;
    }
    deepLinkZipFilledRef.current = true;
    updateZip(deepLinkInitial.zip);
  }, [loanLimits, fromDeepLink, deepLinkInitial]);

  useEffect(() => {
    if (
      !fromDeepLink ||
      queryCountySyncedRef.current ||
      !deepLinkInitial?.state ||
      !deepLinkInitial?.county ||
      !loanLimits[deepLinkInitial.state]
    ) {
      return;
    }

    const matchedCounty = matchCountyInLoanLimits(
      deepLinkInitial.county,
      deepLinkInitial.state,
      loanLimits,
    );
    queryCountySyncedRef.current = true;
    if (matchedCounty) {
      setCounty(matchedCounty);
    }
  }, [loanLimits, fromDeepLink, deepLinkInitial]);

  useEffect(() => {
    // console.log("apply params to current search Fields", historyParams);

    if (historyParams === null) {
      return;
    }
    setFrom(historyParams?.from || "search");
    // apply params to current search Fields
    // loan
    const restoredPurpose = historyParams?.purpose || "";
    setLoanPurpose(restoredPurpose);
    setLoanType(historyParams?.loanType || historyParams?.loanTypes || [""]);
    setPropertyValue(historyParams?.propertyValue || getPropertyValue(historyParams)); // 需要计算 
    setLoanAmount(historyParams?.loanAmount || 0);
    setUpFrontMIP(Number(historyParams?.upFrontMIP) || 1.75); // percent
    setUpFrontMIP_Financed(historyParams?.upFrontMIP_Financed);
    setDownPayment(
      restoredPurpose === "Purchase"
        ? (historyParams?.downPayment ?? (
            Number(historyParams?.propertyValue || 0) - Number(historyParams?.loanAmount || 0)
          ))
        : null,
    );
    setCltv(historyParams?.CLTV ? Number(Number(historyParams.CLTV).toFixed(2)) : 0);
    setHcltv(historyParams?.HCLTV ? Number(Number(historyParams.HCLTV).toFixed(2)) : 0);
    setIsSubordinateLiens(historyParams?.isSubordinateLiens || false);
    setSecondaryLoanAmount(historyParams?.secondaryLoanAmount || 0);

    // property
    setOccupancy(common.transFormOccupancy(historyParams?.occupancy) || "");
    setPropertyType(
      // A non-warrantable condo is saved as the attached-condo enum plus
      // nonWarrantableCondo.
      historyParams?.nonWarrantableCondo
        ? NON_WARRANTABLE_CONDO_PROPERTY_TYPE
        : (common.transFormPropertyType(historyParams?.property) || "")
    );
    setState(historyParams?.state || "");
    setCounty(historyParams?.county || "");

    // borrower
    setFico(historyParams?.FICO || null);
    setNoFico(PPE_FEATURES.noFico && !!historyParams?.noFico && historyParams?.citizenship === FOREIGN_NATIONAL_CITIZENSHIP);
    // A search saved with no income restores with the box blank (plan D3).
    const restoredMonthlyIncome = historyParams?.monthlyIncome ?? null;
    const restoredMonthlyDebt = historyParams?.monthlyDebt ?? null;
    setMonthlyIncome(restoredMonthlyIncome);
    setMonthlyDebt(restoredMonthlyDebt);
    setDti(calculateDti(restoredMonthlyIncome, restoredMonthlyDebt));
    setIsFirstTimeHomeBuyer(historyParams?.firsttimehomebuyer || historyParams?.firstTimeHomeBuyer || false); // firsttimehomebuyer // firstTimeHomeBuyer
    setIsSelfEmployed(historyParams?.selfEmployed || historyParams?.selfemployed || false); // selfemployed // selfEmployed

    // product
    // A search saved by rate restores by rate (11-04); one with no usable
    // rate at the default target price (searchTargetOf).
    const restoredTarget = searchTargetOf(historyParams);
    setSearchBy(restoredTarget.by);
    setTargetPrice(restoredTarget.by === "price" ? restoredTarget.value : DEFAULT_TARGET_PRICE);
    setTargetRate(restoredTarget.by === "rate" ? restoredTarget.value : null);
    // Price or Cost to borrower: the form starts at Price (the user, 11-04).
    // Only a recent search the officer picked to rerun (from "history": the
    // list under the form or the header's list) keeps its own; coming back
    // from the results (Refine search, a change suggestion) goes back to
    // Price.
    setTargetBasis(historyParams?.from === "history" ? targetPriceBasisOf(historyParams) : TARGET_BASIS_PRICE);
    setTargetPriceMessage("");
    setLockDay(Number(historyParams?.lockDay) || 30);
    setCompensationType(historyParams?.compensationType || "");
    setCompensationPercent(historyParams?.compensationPercent || 0); // compensationPercentage // compensationPercent
    setEstimatedClosingFee(historyParams?.estimatedClosingCosts || 0); // estimatedClosingCosts // estimatedClosingFee
    setAusType(historyParams?.ausTypes || []);
    setIsImpound(historyParams?.isImpound || false); // Impound  // escrowWaiver // || historyParams?.escrowWaiver
    setIsInterestOnly(historyParams?.isInterestOnly || false);
    setIsWaiveLenderFee(historyParams?.isWaiveLenderFee || false);

    // PPE: restore the merged + Non-QM fields too. The `body` in check_rates
    // carries them, but without restoring here they'd reset to each field's
    // useState default whenever the collapsed form is reopened to refine a
    // search. Keys mirror that body; numeric/boolean fields use ?? so a valid
    // 0/false survives. Total loan stays null when absent so it can be derived
    // from the restored loan amounts.
    const historyProgram = historyParams?.loanProgram || "conventional";
    const restoredLien = historyParams?.lienPosition === "second" || isSecondLienProgram(historyProgram)
      ? "second"
      : "first";
    const restoredProgram = restoredLien === "second"
      ? (isSecondLienProgram(historyProgram) ? historyProgram : "secondMortgage")
      : (isSecondLienProgram(historyProgram) ? "conventional" : historyProgram);
    // Searches saved before the Loan terms multi-select carry one loanTerm;
    // fixed-period-only ARM values restore as the N/6 product.
    const restoredTerms = selectedLoanTermsOf({
      ...historyParams,
      selectedLoanTerms: (historyParams?.selectedLoanTerms || []).map(restoreArmTerm),
      loanTerm: restoreArmTerm(historyParams?.loanTerm),
    }, restoredProgram);
    setSelectedLoanTerms(
      restoredTerms.length
        ? restoredTerms
        : DEFAULT_LOAN_TERMS,
    );
    setLoanProgram(restoredProgram);
    // Old saved searches may contain an invalid program/lien pair. Normalize
    // both fields before showing or replaying the request.
    setPpeLien(restoredLien);
    // ITIN used to be a box next to Citizenship; it is now a Citizenship
    // choice, offered only for Non-QM (plan D17, D28).
    const restoredItin = historyParams?.citizenship === ITIN_CITIZENSHIP || !!historyParams?.itin;
    setCitizenship(
      restoredItin
        ? (restoredProgram === "nonqm" ? ITIN_CITIZENSHIP : "NON_PERMANENT_RESIDENT_ALIEN")
        : (historyParams?.citizenship || "US_CITIZENS"),
    );
    setVesting(PPE_FEATURES.entityVesting && historyParams?.vesting === "entity" && restoredProgram === "nonqm" ? "entity" : "individual");
    setPpeAppraised(historyParams?.appraisedValue ?? null);
    // Restored appraisal values are inactive until Advanced is opened.
    setPpeAppraisalOverrideActive(false);
    setPpeCashOut(historyParams?.cashOutAmount ?? null);
    setPpeCltv(historyParams?.cltvInput ?? null);
    setPpeHcltv(historyParams?.hcltvInput ?? null);
    setPpeLine(historyParams?.lineAmount ?? PROGRAM_FIELD_DEFAULTS.helocLineAmount);
    setPpeDraw(historyParams?.drawAmount ?? PROGRAM_FIELD_DEFAULTS.helocDrawAmount);
    setPpeDrawTermMonths(historyParams?.drawTermMonths ?? PROGRAM_FIELD_DEFAULTS.helocDrawTermMonths);
    setPpePrimaryMtg(
      historyParams?.primaryMortgageAmount
        ?? PROGRAM_FIELD_DEFAULTS.secondLienPrimaryMortgageAmount,
    );
    // Fall back to the useState defaults, not null: rows saved before these had
    // defaults carry no fundingFee/guaranteeFee, and restoring null re-triggers
    // the "loanProgram=va requires fundingFee" rejection on re-run.
    const restoredVaExempt = historyParams?.vaExemptFundingFee || false;
    setPpeFundingFee(
      restoredVaExempt
        ? 0
        : (historyParams?.fundingFee ?? PROGRAM_FIELD_DEFAULTS.vaFundingFee),
    );
    setPpeVaSubseq(historyParams?.vaSubsequentUse || false);
    setPpeVaExempt(restoredVaExempt);
    setPpeGuaranteeFee(historyParams?.guaranteeFee ?? PROGRAM_FIELD_DEFAULTS.usdaGuaranteeFee);
    setPpeReserves(historyParams?.reserves ?? BORROWER_DEFAULTS.reserves);
    setPpePropsFinanced(
      historyParams?.propertiesFinanced ?? BORROWER_DEFAULTS.propertiesFinanced,
    );
    setPpeUnits(historyParams?.numberOfUnits ?? 1);
    // Restored as saved: the ZIP is looked up but State/County are not
    // refilled from it (a mismatch is only flagged under the field).
    setPpeZip(historyParams?.propertyZip || "");
    setZipNote("");
    setPpePrepay(historyParams?.prepaymentPenalty ?? 0);
    setPpeInterestOnly(historyParams?.interestOnlyNew || false);

    const nq = historyParams?.nonQm;
    if (nq) {
      const restoredIncomeDoc = (nq.incomeDocumentation && nq.incomeDocumentation[0]) || "FullDoc";
      const restoredDocumentation = restoredIncomeDoc === "NoRatio" ? "DebtServiceCoverageRatio" : restoredIncomeDoc;
      const restoredDscrMode = (
        restoredIncomeDoc === "DebtServiceCoverageRatio" && nq.dscrMode === "noRatio"
          ? "noRatio"
          : "number"
      );
      if (isSecondLienProgram(restoredProgram)) {
        setSecondIncomeDoc(restoredDocumentation);
        setSecondDscrMode(restoredDscrMode);
        setSecondDscr(nq.dscr ?? 1.2);
      } else {
        setNqIncomeDoc(restoredDocumentation);
        setNqDscrMode(restoredDscrMode);
        setNqDscr(nq.dscr ?? 1.2);
      }
      const restoredHousingEvent = nq.housingEventType || "";
      const restoredBkType = nq.bankruptcyType || "";
      setNqHousingEvent(restoredHousingEvent);
      setNqHousingSeasoning(
        restoredHousingEvent
          ? (normalizeSeasoningBucket(nq.housingEventSeasoning) || DEFAULT_SEASONING_BUCKET)
          : ""
      );
      setNqBkType(restoredBkType);
      setNqBkOutcome(
        restoredBkType
          ? (normalizeBankruptcyOutcome(nq.bankruptcyOutcome) || DEFAULT_BANKRUPTCY_OUTCOME)
          : ""
      );
      setNqBkSeasoning(
        restoredBkType
          ? (normalizeSeasoningBucket(nq.bankruptcySeasoning) || DEFAULT_SEASONING_BUCKET)
          : ""
      );
      // Older saved searches always contain an all-zero mortgageLates object.
      // Restore "Yes" only when at least one actual late was recorded.
      setNqHasMortgageLates(hasMortgageLateCounts(nq.mortgageLates));
      const l12 = nq.mortgageLates?.last12mo || {};
      const l24 = nq.mortgageLates?.m13to24 || {};
      setNqLates({
        l12_30: l12.x30 || 0, l12_60: l12.x60 || 0, l12_90: l12.x90 || 0, l12_120: l12.x120 || 0,
        l24_30: l24.x30 || 0, l24_60: l24.x60 || 0, l24_90: l24.x90 || 0, l24_120: l24.x120 || 0,
      });
      setNqShortTermRental(nq.shortTermRental || false);
    } else {
      setNqIncomeDoc("FullDoc");
      setNqDscrMode("number");
      setNqDscr(1.2);
      setSecondIncomeDoc("FullDoc");
      setSecondDscrMode("number");
      setSecondDscr(1.2);
      setNqHasMortgageLates(false);
      setNqLates({ l12_30: 0, l12_60: 0, l12_90: 0, l12_120: 0, l24_30: 0, l24_60: 0, l24_90: 0, l24_120: 0 });
    }
  }, [historyParams]);

  useEffect(() => {
    if (!closingFeeConfig) return;

    const lodeStar = lodeStarConfig || {};
    const shouldCalculate =
      lodeStar.enableCanShopServices === false &&
      lodeStar.enableTaxesAndOtherGovernmentFees === false &&
      lodeStar.enableOther === false;

    setIsCalculatedAtResults(!shouldCalculate);

    if (shouldCalculate) {
      const calculateArrayTotal = (arr) => {
        if (!arr) return 0;
        return arr.reduce(
          (sum, item) => sum + (Number(item.estimateAmount) || 0),
          0
        );
      };

      const total =
        calculateArrayTotal(closingFeeConfig.canNotShopServices) +
        calculateArrayTotal(closingFeeConfig.canShopServices) +
        calculateArrayTotal(closingFeeConfig.taxesAndOtherGovernmentFees) +
        calculateArrayTotal(closingFeeConfig.prepaids) +
        calculateArrayTotal(closingFeeConfig.initialEscrowPaymentsAtClosing) +
        calculateArrayTotal(closingFeeConfig.other) +
        calculateArrayTotal(closingFeeConfig.originationCharges);

      setEstimatedClosingFee(total);
    } else {
      setEstimatedClosingFee("Calculated at results");
    }
  }, [lodeStarConfig, closingFeeConfig]);

  // useEffect(() => {
  //   setUpFrontMIP(Number(loanAmount || 0) * 0.0175);
  // }, [loanAmount]);

  const showCounties = () => {
    let options = [
    // <option value="">Select your county</option>
    ];
    if (state && state in loanLimits) {
      let keys = Object.keys(loanLimits[state]);
      const countyOption = (x) => (
        <option key={x} value={x}>{com.capitalize(x.replace("COUNTY", ""))}</option>
      );
      // A known ZIP's counties go first; picking one of the others clears the ZIP.
      const inZip = zipCountiesInState(currentZipCounties(), state, loanLimits);
      if (inZip.length > 0) {
        return [
          <Select.OptGroup key="in-zip" label={`In ZIP ${zip5(ppeZip)}`}>
            {inZip.map(countyOption)}
          </Select.OptGroup>,
          <Select.OptGroup key="other" label="Other counties (clears the ZIP)">
            {keys.filter((x) => !inZip.includes(x)).map(countyOption)}
          </Select.OptGroup>,
        ];
      }
      keys.forEach((x) => {
        options.push(countyOption(x));
      });
    }
    return options;
  };

  // The looked-up counties of the ZIP in the field, or null while unknown.
  const currentZipCounties = () => (
    zipLookup && zipLookup.zip === zip5(ppeZip) ? zipLookup.counties : null
  );

  latestFieldsRef.current = { ppeZip, state, county, loanLimits };

  // Typing a full ZIP fills State and County with its main county, unless the
  // county already shown is one of the ZIP's.
  const updateZip = (value) => {
    setPpeZip(value);
    setZipNote("");
    const zip = zip5(value);
    if (!zip) return;
    lookupZipCounties(zip)
      .then((counties) => {
        const latest = latestFieldsRef.current;
        if (zip5(latest.ppeZip) !== zip) return;
        const next = locationForZip(counties, latest, latest.loanLimits);
        if (!next) {
          setZipNote(`ZIP ${zip} is not in the Census ZIP list; check State and County yourself.`);
          return;
        }
        setState(next.state);
        setCounty(next.county);
      })
      .catch((err) => console.error(err));
  };

  const zipFieldNote = () => {
    const counties = currentZipCounties();
    if (!zipMatchesLocation(counties, state, county)) {
      return (
        <div className="ppe-zip-note ppe-zip-note-error">
          {`ZIP ${zip5(ppeZip)} is in ${counties.map((c) => describeLocation(c.state, c.county)).join(" or ")}, not the county selected.`}
        </div>
      );
    }
    return zipNote ? <div className="ppe-zip-note">{zipNote}</div> : null;
  };

  const closeModal_no_lender = () => {
    setIsOpen_no_lender(false);
    // console.log("closeModal_no_lender");
  };

  const loan_svg = () => {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
      >
        <path
          d="M11.1337 5.33333C11.001 4.95683 10.7586 4.62864 10.4378 4.39102C10.1171 4.1534 9.73249 4.01722 9.33366 4H6.66699C6.13656 4 5.62785 4.21071 5.25278 4.58579C4.87771 4.96086 4.66699 5.46957 4.66699 6C4.66699 6.53043 4.87771 7.03914 5.25278 7.41421C5.62785 7.78929 6.13656 8 6.66699 8H9.33366C9.86409 8 10.3728 8.21071 10.7479 8.58579C11.1229 8.96086 11.3337 9.46957 11.3337 10C11.3337 10.5304 11.1229 11.0391 10.7479 11.4142C10.3728 11.7893 9.86409 12 9.33366 12H6.66699C6.26816 11.9828 5.8836 11.8466 5.56281 11.609C5.24203 11.3714 4.99969 11.0432 4.86699 10.6667M8.00033 2V4M8.00033 12V14"
          stroke="#1630AC"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    );
  };

  const property_svg = () => {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
      >
        <path
          d="M2.55556 8H1L8 1L15 8H13.4444"
          stroke="#1630AC"
          stroke-width="1.4"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M2.55556 8V13.4444C2.55556 13.857 2.71944 14.2527 3.01117 14.5444C3.30289 14.8361 3.69855 15 4.11111 15H11.8889C12.3014 15 12.6971 14.8361 12.9888 14.5444C13.2806 14.2527 13.4444 13.857 13.4444 13.4444V8"
          stroke="#1630AC"
          stroke-width="1.4"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M6.44444 8H9.55556V11.1111H6.44444V8Z"
          stroke="#1630AC"
          stroke-width="1.4"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    );
  };

  const borrower_svg = () => {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
      >
        <path
          d="M4 14V12.6667C4 11.9594 4.28095 11.2811 4.78105 10.781C5.28115 10.281 5.95942 10 6.66667 10H9.33333C10.0406 10 10.7189 10.281 11.219 10.781C11.719 11.2811 12 11.9594 12 12.6667V14M5.33333 4.66667C5.33333 5.37391 5.61428 6.05219 6.11438 6.55229C6.61448 7.05238 7.29276 7.33333 8 7.33333C8.70724 7.33333 9.38552 7.05238 9.88562 6.55229C10.3857 6.05219 10.6667 5.37391 10.6667 4.66667C10.6667 3.95942 10.3857 3.28115 9.88562 2.78105C9.38552 2.28095 8.70724 2 8 2C7.29276 2 6.61448 2.28095 6.11438 2.78105C5.61428 3.28095 5.33333 3.95942 5.33333 4.66667Z"
          stroke="#1630AC"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    );
  };

  const product_svg = () => {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="16"
        viewBox="0 0 18 16"
        fill="none"
      >
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M7.0918 3.50059C7.0918 2.58932 7.83396 1.85059 8.74947 1.85059H9.2518C10.1673 1.85059 10.9095 2.58932 10.9095 3.50059V9.83392C10.9095 10.7452 10.1673 11.4839 9.2518 11.4839H8.74947C7.83396 11.4839 7.0918 10.7452 7.0918 9.83392V3.50059ZM8.74947 3.15059C8.55527 3.15059 8.39784 3.30729 8.39784 3.50059V9.83392C8.39784 10.0272 8.55527 10.1839 8.74947 10.1839H9.2518C9.446 10.1839 9.60342 10.0272 9.60342 9.83392V3.50059C9.60342 3.30729 9.446 3.15059 9.2518 3.15059H8.74947Z"
          fill="#1630AC"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M2.31934 6.00059C2.31934 5.08932 3.0615 4.35059 3.97701 4.35059H4.47934C5.39484 4.35059 6.13701 5.08932 6.13701 6.00059V10.0006C6.13701 10.9119 5.39484 11.6506 4.47934 11.6506H3.97701C3.0615 11.6506 2.31934 10.9119 2.31934 10.0006V6.00059ZM3.97701 5.65059C3.78281 5.65059 3.62538 5.80729 3.62538 6.00059V10.0006C3.62538 10.1939 3.78281 10.3506 3.97701 10.3506H4.47934C4.67353 10.3506 4.83096 10.1939 4.83096 10.0006V6.00059C4.83096 5.80729 4.67353 5.65059 4.47934 5.65059H3.97701Z"
          fill="#1630AC"
        />
        <rect
          x="16.0322"
          y="12.5"
          width="1.335"
          height="14.0651"
          rx="0.6675"
          transform="rotate(90 16.0322 12.5)"
          fill="#1630AC"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M11.8643 7.50059C11.8643 6.58932 12.6064 5.85059 13.5219 5.85059H14.0243C14.9398 5.85059 15.6819 6.58932 15.6819 7.50059V10.0006C15.6819 10.9119 14.9398 11.6506 14.0243 11.6506H13.5219C12.6064 11.6506 11.8643 10.9119 11.8643 10.0006V7.50059ZM13.5219 7.15059C13.3277 7.15059 13.1703 7.30729 13.1703 7.50059V10.0006C13.1703 10.1939 13.3277 10.3506 13.5219 10.3506H14.0243C14.2185 10.3506 14.3759 10.1939 14.3759 10.0006V7.50059C14.3759 7.30729 14.2185 7.15059 14.0243 7.15059H13.5219Z"
          fill="#1630AC"
        />
      </svg>
    );
  };

  const edit_svg = () => {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="17"
        height="16"
        viewBox="0 0 17 16"
        fill="none"
      >
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M2.96245 3.47627C2.77885 3.47627 2.60278 3.5492 2.47296 3.67902C2.34314 3.80884 2.27021 3.98491 2.27021 4.1685V13.8598C2.27021 14.0434 2.34314 14.2195 2.47296 14.3493C2.60278 14.4791 2.77885 14.552 2.96245 14.552H12.6537C12.8373 14.552 13.0134 14.4791 13.1432 14.3493C13.273 14.2195 13.346 14.0434 13.346 13.8598V11.0909C13.346 10.7085 13.6559 10.3986 14.0382 10.3986C14.4205 10.3986 14.7304 10.7085 14.7304 11.0909V13.8598C14.7304 14.4106 14.5116 14.9388 14.1222 15.3282C13.7327 15.7177 13.2045 15.9365 12.6537 15.9365H2.96245C2.41167 15.9365 1.88345 15.7177 1.49399 15.3282C1.10454 14.9388 0.885742 14.4106 0.885742 13.8598V4.1685C0.885742 3.61773 1.10454 3.08951 1.49399 2.70005C1.88345 2.31059 2.41167 2.0918 2.96245 2.0918H5.73139C6.1137 2.0918 6.42362 2.40172 6.42362 2.78403C6.42362 3.16634 6.1137 3.47627 5.73139 3.47627H2.96245Z"
          fill="#325CEB"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M12.0257 0.910759C12.2961 0.640424 12.7343 0.640424 13.0047 0.910759L15.9121 3.81814C16.1814 4.08748 16.1825 4.5238 15.9146 4.79454L9.3384 11.44C9.21586 11.5638 9.05113 11.6369 8.87709 11.6446L5.76203 11.7831C5.56529 11.7918 5.37413 11.7164 5.23642 11.5756C5.09872 11.4348 5.0275 11.242 5.0406 11.0455L5.24827 7.93043C5.25942 7.76312 5.33092 7.60555 5.44949 7.48699L12.0257 0.910759ZM12.5152 2.37921L6.61237 8.28204L6.47346 10.3656L8.54492 10.2736L14.4462 4.31018L12.5152 2.37921Z"
          fill="#325CEB"
        />
      </svg>
    );
  };

  // 添加新的状态变量
  const [cltv, setCltv] = useState(0);
  const [hcltv, setHcltv] = useState(0);

  // 添加新的更新函数
  const updateCltv = (value) => {
    setCltv(value);
  };

  const updateHcltv = (value) => {
    setHcltv(value);
  };

  // PPE: CLTV/HCLTV are "everything secured by the property / property value".
  // The two differ only for a HELOC subordinate: CLTV counts the active DRAW,
  // HCLTV counts the FULL credit line (undrawn portion included), because the
  // borrower can draw the rest at any time. With no subordinate financing all
  // three ratios collapse to LTV, which is why this went unnoticed.
  //
  // Single source of truth: both the inputs below and the request body read this,
  // so what the form shows is always what gets priced.
  const getDerivedLoanMetrics = () => calculateLoanMetrics({
    loanProgram,
    loanPurpose,
    propertyValue,
    appraisedValue: getEffectivePpeAppraisedValue(),
    loanAmount: isSecondLienProgram(loanProgram)
      ? loanAmount
      : getDerivedFirstLienAmounts().baseLoanAmount,
    secondaryLoanAmount,
    primaryMortgageAmount: ppePrimaryMtg,
    lineAmount: ppeLine,
    drawAmount: ppeDraw,
    drawTermMonths: ppeDrawTermMonths,
  });

  const getDerivedRatios = () => getDerivedLoanMetrics();

  const getEffectiveRatios = () => resolveLoanMetricOverrides({
    lienPosition: ppeLien,
    derivedMetrics: getDerivedRatios(),
    cltvOverride: ppeCltv,
    hcltvOverride: ppeHcltv,
  });

  // Loan terms: one multi-select box, grouped Fixed / ARM, with "All Fixed" /
  // "All ARM" at the top of the list (user, 11-03). Several can be ticked;
  // the results price each with its own request. At least one stays ticked.
  const updateLoanTerms = (value) => {
    const terms = (Array.isArray(value) ? value : [value]).filter(Boolean);
    if (terms.length) setSelectedLoanTerms(terms);
  };

  const loanTermsSelect = () => {
    const groups = loanProgram === "heloc"
      ? [{ label: "Fixed", options: HELOC_TERM_OPTIONS }]
      : [{ label: "Fixed", options: FIXED_TERM_OPTIONS }, { label: "ARM", options: ARM_TERM_OPTIONS }];
    return (
      <Select
        id="ppe-loan-terms"
        className="ppe-input ppe-loan-terms"
        mode="multiple"
        maxTagCount="responsive"
        showSearch={false}
        value={selectedLoanTerms}
        onChange={updateLoanTerms}
        options={groups.map((group) => ({
          label: group.label,
          options: group.options.map(({ value, label }) => ({ value, label })),
        }))}
        dropdownRender={(menu) => (
          <>
            {groups.length > 1 && (
              <div className="ppe-loan-terms-groups">
                {groups.map((group) => (
                  <Button key={group.label} size="small" type="link" onClick={() => toggleLoanTermGroup(group.options)}>
                    All {group.label}
                  </Button>
                ))}
              </div>
            )}
            {menu}
          </>
        )}
      />
    );
  };

  const field_Loan = () => {
    const fmt = (value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    const prs = (value) => value.replace(/\$\s?|(,*)/g, "");
    const derivedMetrics = getDerivedLoanMetrics();
    const secondLien = isSecondLienProgram(loanProgram);
    return (
      <div className="search-fields-content-item loan_field">
        <div className="search-fields-content-item-title">
          <div className="search-fields-content-item-title-svg">{loan_svg()}</div>
          <span className="search-fields-content-item-title-text">Loan</span>
        </div>
        <div className="search-fields-content-item-content">
          <Field id="ppe-lien-position" title="*Lien position" group>
            <Choice
              id="ppe-lien-position"
              value={ppeLien}
              onChange={updatePpeLien}
              options={[
                { value: "first", label: "First lien" },
                { value: "second", label: "Second lien" },
              ]}
            />
          </Field>
          {/* A list, not buttons: five programs did not fit the column at
              1280 wide with the side menu open ("Conventi…", user 11-04). */}
          <Field id="ppe-loan-program" title="*Loan program">
            <Select id="ppe-loan-program" className="ppe-input" value={loanProgram} onChange={updateLoanProgram} options={LOAN_PROGRAM_OPTIONS_BY_LIEN[ppeLien]} />
          </Field>
          <Field id="ppe-loan-purpose" title="*Loan purpose" group>
            <Choice id="ppe-loan-purpose" value={loanPurpose} onChange={updateLoanPurpose} options={LOAN_PURPOSE_OPTIONS} />
          </Field>
          <Field
            id="ppe-loan-terms"
            title={loanProgram === "heloc" ? "*Repayment term" : "*Loan terms"}
            tip={loanProgram === "heloc" ? undefined : "Tick several to compare them; each term is priced on its own."}
            tipLabel="About loan terms"
          >
            {loanTermsSelect()}
          </Field>
          {loanProgram === "heloc" && (
            <Field
              id="ppe-draw-term"
              title="*Draw term (years)"
              tip="A HELOC is a variable-rate line. The draw term is separate from the 15, 25 or 30-year repayment term."
              tipLabel="About the draw term"
              group
            >
              <Choice
                id="ppe-draw-term"
                value={ppeDrawTermMonths}
                onChange={setPpeDrawTermMonths}
                options={[24, 36, 60, 120].map((months) => ({ value: months, label: String(months / 12), title: `${months / 12} years (${months} months)` }))}
              />
            </Field>
          )}
          <Field id="ppe-property-value" title={loanPurpose === "Purchase" ? "*Property value" : "*Home value"} error={propteryError}>
            <InputNumber id="ppe-property-value" className="ppe-input" prefix="$" placeholder="Property value"
              aria-describedby={propteryError ? "ppe-property-value-error" : undefined}
              value={propertyValue} onChange={updatePropertyValue} status={propteryError ? "error" : ""} formatter={fmt} parser={prs} />
          </Field>
          {secondLien && (
            <Field id="ppe-appraised-value" title="Appraised value">
              <InputNumber id="ppe-appraised-value" className="ppe-input" prefix="$" value={ppeAppraised ?? propertyValue} onChange={updatePpeAppraised} formatter={fmt} parser={prs} />
            </Field>
          )}
          {secondLien && (
            <div className="ppe-subhead">
              {loanProgram === "heloc" ? "HELOC structure" : "Loan structure"}
              <Tip
                label={loanProgram === "heloc" ? "About the HELOC structure" : "About the loan structure"}
                title={loanProgram === "heloc"
                  ? "Loan balance pricing uses the line amount minus the draw; the full line is used for HCLTV and the draw for CLTV."
                  : "The second-lien balance is included in both CLTV and HCLTV."}
              />
            </div>
          )}
          {loanProgram === "secondMortgage" && (
            <Field id="ppe-loan-amount" title="*Loan amount">
              <InputNumber id="ppe-loan-amount" className="ppe-input" prefix="$" placeholder="Loan amount" value={loanAmount} onChange={updateLoanAmount} formatter={fmt} parser={prs} />
            </Field>
          )}
          {!isSecondLienProgram(loanProgram) && (
            <div className="ppe-field-pair">
              {loanPurpose === "Purchase" ? (
                <Field id="ppe-down-payment" title="Down payment (1st)">
                  <InputNumber id="ppe-down-payment" aria-label="Down payment" className="ppe-input" prefix="$" placeholder="Down payment" value={downPayment} onChange={updateDownPayment} formatter={fmt} parser={prs} />
                </Field>
              ) : (
                <Field id="ppe-first-loan-amount" title="*Loan amount (1st)">
                  <InputNumber id="ppe-first-loan-amount" aria-label="Loan amount" className="ppe-input" prefix="$" placeholder="Loan amount" value={loanAmount} onChange={updateLoanAmount} formatter={fmt} parser={prs} />
                </Field>
              )}
              <Field id="ppe-ltv" title="LTV">
                <InputNumber id="ppe-ltv" aria-label="LTV" className="ppe-input" placeholder="LTV" suffix="%" value={getDerivedLoanMetrics().ltv} onChange={updateLtv} />
              </Field>
            </div>
          )}
          {loanProgram === "fha" && (
            <Field id="ppe-ufmip" title="UFMIP" tip="Upfront mortgage insurance premium, as a percent of the base loan amount." tipLabel="About UFMIP">
              <InputNumber id="ppe-ufmip" className="ppe-input" suffix="%" value={upFrontMIP} onChange={setUpFrontMIP} />
            </Field>
          )}
          {loanProgram === "va" && (
            <>
              <Field id="ppe-funding-fee" title="Funding fee">
                <InputNumber id="ppe-funding-fee"
                  className="ppe-input"
                  suffix="%"
                  min={ppeVaExempt ? 0 : 0.01}
                  disabled={ppeVaExempt}
                  value={ppeFundingFee}
                  onChange={setPpeFundingFee}
                  onBlur={() => {
                    if (!ppeVaExempt && !(Number(ppeFundingFee) > 0)) {
                      message.error("Funding fee must be greater than 0 unless VA exempt funding fee is ticked.");
                      setPpeFundingFee(PROGRAM_FIELD_DEFAULTS.vaFundingFee);
                    }
                  }}
                />
              </Field>
              <div className="search-fields-content-item-content-item ppe-checks">
                <Checkbox checked={ppeVaSubseq} onChange={(e) => setPpeVaSubseq(e.target.checked)}>VA subsequent use</Checkbox>
                <Checkbox checked={ppeVaExempt} onChange={updateVaExemptFundingFee}>VA exempt funding fee</Checkbox>
              </div>
            </>
          )}
          {loanProgram === "usda" && (
            <Field id="ppe-usda-g-fee" title="USDA guarantee fee">
              <InputNumber id="ppe-usda-g-fee" className="ppe-input" suffix="%" value={ppeGuaranteeFee} onChange={setPpeGuaranteeFee} />
            </Field>
          )}
          {loanProgram === "heloc" && (
            <Field
              id="ppe-line-amount"
              title="*Line amount"
              // A cash-out HELOC has no separate Cash-out amount (plan D35):
              // the draw is the cash, and the line is limited by CLTV/HCLTV
              // and the lender's line bands.
              tip={loanPurpose === "CashOutRefi" ? "The line is limited by CLTV/HCLTV and the lender's line amount bands, not by a separate cash-out amount." : undefined}
              tipLabel="About the line amount"
            >
              <InputNumber id="ppe-line-amount"
                className="ppe-input"
                prefix="$"
                min={0}
                status={ppeHelocAmountError ? "error" : ""}
                value={ppeLine}
                onChange={(value) => {
                  setPpeLine(value);
                  validatePpeHelocAmounts(value, ppeDraw);
                }}
                formatter={fmt}
                parser={prs}
              />
            </Field>
          )}
          {loanProgram === "heloc" && (
            <Field id="ppe-draw" title="Draw" error={ppeHelocAmountError}>
              <InputNumber id="ppe-draw"
                className="ppe-input"
                prefix="$"
                min={0}
                max={Number(ppeLine) || 0}
                status={ppeHelocAmountError ? "error" : ""}
                aria-describedby={ppeHelocAmountError ? "ppe-draw-error" : undefined}
                value={ppeDraw}
                onChange={(value) => {
                  setPpeDraw(value);
                  validatePpeHelocAmounts(ppeLine, value);
                }}
                formatter={fmt}
                parser={prs}
              />
            </Field>
          )}
          {(loanProgram === "secondMortgage" || ppeLien === "second") && (
            <Field id="ppe-first-mortgage" title={loanPurpose === "Purchase" ? "First mortgage amount" : "Existing first mortgage balance"}>
              <InputNumber id="ppe-first-mortgage" className="ppe-input" prefix="$" value={ppePrimaryMtg} onChange={setPpePrimaryMtg} formatter={fmt} parser={prs} />
            </Field>
          )}
          {secondLien && (
            <div className="ppe-field-pair ppe-field-pair-even">
              <Field id="ppe-cltv" title="CLTV" className="second-lien-ratio-field">
                <ReadOnlyValue id="ppe-cltv">{formatPercent(derivedMetrics.cltv)}</ReadOnlyValue>
              </Field>
              {loanProgram === "heloc" && (
                <Field id="ppe-hcltv" title="HCLTV" className="second-lien-ratio-field">
                  <ReadOnlyValue id="ppe-hcltv">{formatPercent(derivedMetrics.hcltv)}</ReadOnlyValue>
                </Field>
              )}
            </div>
          )}
          {loanPurpose === "CashOutRefi" && loanProgram !== "heloc" && (
            <Field id="ppe-cash-out" title="*Cash-out amount" error={ppeCashOutError}>
              <InputNumber id="ppe-cash-out"
                className="ppe-input"
                prefix="$"
                min={0}
                value={ppeCashOut}
                onChange={updatePpeCashOut}
                status={ppeCashOutError ? "error" : ""}
                aria-describedby={ppeCashOutError ? "ppe-cash-out-error" : undefined}
                formatter={fmt}
                parser={prs}
              />
            </Field>
          )}
          <AdvancedLoanFields
            lienPosition={ppeLien}
            open={ppeAdvOpen}
            onToggle={toggleAdvanced}
            appraisedValue={getEffectivePpeAppraisedValue()}
            totalLoanAmount={getDerivedFirstLienAmounts().totalLoanAmount}
            secondaryLoanAmount={secondaryLoanAmount}
            cltv={getEffectiveRatios().cltv}
            hcltv={getEffectiveRatios().hcltv}
            onAppraisedValueChange={updatePpeAppraised}
            onTotalLoanAmountChange={setPpeTotalLoan}
            onSecondaryLoanAmountChange={updateSecondaryLoanAmount}
            onCltvChange={setPpeCltv}
            onHcltvChange={setPpeHcltv}
          />
        </div>
      </div>
    );
  };

  const field_Property = () => {
    const multifamily = propertyType === MULTIFAMILY_PROPERTY_TYPE;
    const unitOptions = [1, 2, 3, 4].map((units) => ({
      value: units,
      label: String(units),
      // 2-4 unit multifamily takes 2 to 4 units; every other type 1.
      disabled: multifamily ? units === 1 : units !== 1,
    }));
    // A value saved outside 1-4 still shows, so the box never looks empty.
    if (ppeUnits && !unitOptions.some((o) => o.value === Number(ppeUnits))) {
      unitOptions.push({ value: Number(ppeUnits), label: String(ppeUnits) });
    }
    return (
      <div className="search-fields-content-item property_field">
        <div className="search-fields-content-item-title">
          <div className="search-fields-content-item-title-svg">
            {property_svg()}
          </div>
          <span className="search-fields-content-item-title-text">
            Property
          </span>
        </div>
        <div className="search-fields-content-item-content">
          <Field id="ppe-occupancy" title="Occupancy" group>
            <Choice
              id="ppe-occupancy"
              value={occupancy}
              onChange={updateOccupancy}
              options={[
                { value: "principal", label: "Primary", title: "Primary residence" },
                { value: "secondhome", label: "Second home" },
                { value: "investment", label: "Investment" },
              ]}
            />
          </Field>
          <Field id="ppe-property-type" title="Property type">
            <Select
              id="ppe-property-type"
              className="ppe-input"
              placeholder="Select"
              value={propertyType}
              onChange={updatePropertyType}
              options={PROPERTY_TYPE_OPTIONS}
            />
          </Field>
          <Field id="ppe-units" title="*Units" group>
            <Choice id="ppe-units" value={Number(ppeUnits)} onChange={setPpeUnits} options={unitOptions} />
          </Field>
          {/* Zip first (plan D10): it fills State and County. The note under
              it is kept: it says what the ZIP changed or that it disagrees. */}
          <Field id="ppe-zip" title="Property ZIP" className="ppe-zip-field">
            <Input id="ppe-zip" className="ppe-input" value={ppeZip} onChange={(e) => updateZip(e.target.value)} placeholder="ZIP" />
            {zipFieldNote()}
          </Field>
          <Field id="ppe-state" title="State">
            <Select
              id="ppe-state"
              className="ppe-input"
              placeholder="Type or select"
              showSearch
              filterOption={matchStateOption}
              value={state}
              onChange={updateState}
              options={STATE_OPTIONS}
            />
          </Field>
          <Field id="ppe-county" title="County">
            <Select
              id="ppe-county"
              className="ppe-input"
              placeholder="Type or select"
              showSearch
              filterOption={matchCountyOption}
              value={county}
              onChange={updateCounty}
            >
              {showCounties()}
            </Select>
          </Field>
          {loanProgram === "nonqm" && (
            <Fold id="nonqm-property" title="Non-QM property details" setCount={nqShortTermRental ? 1 : 0}>
              <div className="search-fields-content-item-content-item ppe-checks">
                <Checkbox checked={nqShortTermRental} onChange={(e) => setNqShortTermRental(e.target.checked)}>Short-term rental</Checkbox>
              </div>
              {/* Vacant / Unleased and Unique Property are not offered: the
                  production calculator answers 400 for them (plan D15). They
                  come back here once a lender rule is in the capability list. */}
            </Fold>
          )}
        </div>
      </div>
    );
  };
  const isDscrSelected = () => INCOME_DOCUMENTATION_PROGRAMS.includes(loanProgram)
    && (isSecondLienProgram(loanProgram) ? secondIncomeDoc : nqIncomeDoc) === DSCR_DOCUMENTATION;

  const moneyFormatter = (value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const moneyParser = (value) => value.replace(/\$\s?|(,*)/g, '');

  const field_Borrower = () => {
    const moreBorrowerSet = [
      Number(ppeReserves) !== BORROWER_DEFAULTS.reserves,
      Number(ppePropsFinanced) !== BORROWER_DEFAULTS.propertiesFinanced,
      isFirstTimeHomeBuyer,
      isSelfEmployed,
    ].filter(Boolean).length;
    const nonQmBorrowerSet = [
      !!nqHousingEvent,
      !!nqBkType,
      nqHasMortgageLates,
      PPE_FEATURES.entityVesting && vesting === "entity",
    ].filter(Boolean).length;
    return (
      <div className="search-fields-content-item borrower_field">
        <div className="search-fields-content-item-title">
          <div className="search-fields-content-item-title-svg">
            {borrower_svg()}
          </div>
          <span className="search-fields-content-item-title-text">
            Borrower
          </span>
        </div>
        <div className="search-fields-content-item-content">
          <Field
            id="ppe-fico"
            title="*Est FICO"
            error={ficoError}
            // No FICO (plan D11): a foreign national with no U.S. credit
            // score; lenders price it only where their rules allow.
            end={PPE_FEATURES.noFico && citizenship === FOREIGN_NATIONAL_CITIZENSHIP && (
              <Checkbox className="ppe-no-fico" checked={noFico} onChange={updateNoFico}>No FICO</Checkbox>
            )}
          >
            <InputNumber
              id="ppe-fico"
              className="ppe-input"
              placeholder={noFico ? "No FICO" : `${CREDIT_SCORE_MIN} to ${CREDIT_SCORE_MAX}`}
              controls={false}
              disabled={noFico}
              value={noFico ? null : fico}
              onChange={updateFico}
              status={ficoError ? "error" : ""}
              aria-describedby={ficoError ? "ppe-fico-error" : undefined}
            />
          </Field>
          {/* Income is optional: blank means the borrower is treated as
              qualifying on income (plan D3). DSCR prices on the property's
              rent, so the boxes are not shown for it (D23). */}
          {!isDscrSelected() && (
            <Field id="ppe-monthly-income" title="Monthly income">
              <InputNumber
                id="ppe-monthly-income"
                className="ppe-input"
                prefix="$"
                placeholder="Optional"
                min={0}
                value={monthlyIncome}
                onChange={updateMonthlyIncome}
                formatter={moneyFormatter}
                parser={moneyParser}
              />
            </Field>
          )}
          {!isDscrSelected() && (
            <div className="ppe-field-pair">
              <Field id="ppe-monthly-debt" title="Monthly debt">
                <InputNumber
                  id="ppe-monthly-debt"
                  className="ppe-input"
                  prefix="$"
                  placeholder="Optional"
                  min={0}
                  value={monthlyDebt}
                  onChange={updateMonthlyDebt}
                  formatter={moneyFormatter}
                  parser={moneyParser}
                />
              </Field>
              <Field
                id="ppe-existing-dti"
                title="Existing DTI"
                tip="Monthly debt ÷ monthly income, before this loan's payment. DTI with the new payment is checked for each rate."
                tipLabel="About Existing DTI"
              >
                <ReadOnlyValue id="ppe-existing-dti" muted={dti === null}>
                  {dti === null ? "—" : `${dti.toFixed(2)}%`}
                </ReadOnlyValue>
              </Field>
            </div>
          )}
          <Field id="ppe-citizenship" title="*Citizenship">
            <Select
              id="ppe-citizenship"
              className="ppe-input"
              placeholder="Select"
              value={citizenship}
              onChange={updateCitizenship}
              options={CITIZENSHIP_OPTIONS.map((option) => (
                option.value === ITIN_CITIZENSHIP
                  ? { ...option, disabled: loanProgram !== "nonqm", title: loanProgram !== "nonqm" ? "ITIN is offered only for Non-QM" : option.label }
                  : option
              ))}
            />
          </Field>
          <Fold id="more-borrower" title="More borrower options" setCount={moreBorrowerSet}>
            <div className="ppe-field-pair ppe-field-pair-even">
              <Field id="ppe-reserves" title="Reserves (months)">
                <InputNumber id="ppe-reserves" className="ppe-input" min={0} value={ppeReserves} onChange={setPpeReserves} />
              </Field>
              <Field id="ppe-properties-financed" title="Properties financed">
                <InputNumber id="ppe-properties-financed" className="ppe-input" min={0} value={ppePropsFinanced} onChange={setPpePropsFinanced} />
              </Field>
            </div>
            <div className="search-fields-content-item-content-item ppe-checks">
              <Checkbox checked={isFirstTimeHomeBuyer} onChange={updateFirstTimeHomeBuyer}>
                First-time buyer
              </Checkbox>
              <Checkbox checked={isSelfEmployed} onChange={updateSelfEmployed}>
                Self-employed
              </Checkbox>
            </div>
            {/* Non-occupant co-borrower is not offered: the production
                calculator answers 400 for it (plan D15). */}
          </Fold>
          {loanProgram === "nonqm" && (
            <Fold id="nonqm-borrower" title="Non-QM credit & investor" setCount={nonQmBorrowerSet}>
              {field_NonQMCredit()}
            </Fold>
          )}
        </div>
      </div>
    );
  };
  const handleEditCompensation = () => {
    setAddLenderModal(true);
  };

  const handleEditCompensationModal = () => {
    console.log("handleEditCompensationModal");
  };

  const reset_all = () => {
    // loan
    setLoanPurpose("Purchase");
    setSelectedLoanTerms(DEFAULT_LOAN_TERMS);
    setLoanType(["conventional"]);
    setLoanProgram("conventional");
    setPpeLien("first");
    setPropertyValue(FIRST_LIEN_PURCHASE_DEFAULTS.propertyValue);
    setDownPayment(FIRST_LIEN_PURCHASE_DEFAULTS.downPayment);
    setLoanAmount(FIRST_LIEN_PURCHASE_DEFAULTS.loanAmount);
    setUpFrontMIP(1.75);
    setUpFrontMIP_Financed(true);
    setSecondaryLoanAmount(FIRST_LIEN_PURCHASE_DEFAULTS.secondaryLoanAmount);
    setIsSubordinateLiens(false);
    setLienType();
    setLienPosition();
    setHelocLineAmount(0);
    setHelocDrawAmount(0);
    setHelocLtv(0);
    setCltv(0);
    setHcltv(0);
    setPpeAppraised(null);
    setPpeAppraisalOverrideActive(false);
    setPpeTotalLoan(null);
    setPpeCashOut(null);
    setPpeCltv(null);
    setPpeHcltv(null);
    setPpeLine(PROGRAM_FIELD_DEFAULTS.helocLineAmount);
    setPpeDraw(PROGRAM_FIELD_DEFAULTS.helocDrawAmount);
    setPpePrimaryMtg(PROGRAM_FIELD_DEFAULTS.secondLienPrimaryMortgageAmount);

    // property
    setOccupancy("principal");
    setPropertyType("singlefamilydetached");
    setPpeUnits(1); // keep Units coherent with the property type being reset
    setPpeZip("");
    setZipNote("");
    setState("CALIFORNIA");
    setCounty("ALAMEDA COUNTY");

    // borrower: no FICO, income or debt by default (plan D2, D3)
    setFico(null);
    setNoFico(false);
    setMonthlyIncome(null);
    setMonthlyDebt(null);
    setDti(null);
    setPpeReserves(BORROWER_DEFAULTS.reserves);
    setPpePropsFinanced(BORROWER_DEFAULTS.propertiesFinanced);
    setCitizenship("US_CITIZENS");
    setIsFirstTimeHomeBuyer(false);
    setIsSelfEmployed(false);
    setVesting("individual");

    // product
    setSearchBy("price");
    setTargetPrice(DEFAULT_TARGET_PRICE);
    setTargetRate(null);
    setTargetBasis(TARGET_BASIS_PRICE);
    setLockDay(30);
    setCompensationType("lender_paid");
    setCompensationPercent(0);
    setEstimatedClosingFee(0);
    setAusType([]);
    setIsImpound(true);
    setIsInterestOnly(false);
    setIsWaiveLenderFee(false);
    setPpePrepay(0);
    setPpeInterestOnly(false);
    setPpeAdvOpen(false);
    setNqIncomeDoc("FullDoc");
    setNqDscrMode("number");
    setNqDscr(1.2);
    setSecondIncomeDoc("FullDoc");
    setSecondDscrMode("number");
    setSecondDscr(1.2);
    setNqHousingEvent("");
    setNqHousingSeasoning("");
    setNqBkType("");
    setNqBkOutcome("");
    setNqBkSeasoning("");
    setNqHasMortgageLates(false);
    setNqLates({ l12_30: 0, l12_60: 0, l12_90: 0, l12_120: 0, l24_30: 0, l24_60: 0, l24_90: 0, l24_120: 0 });
    setNqShortTermRental(false);
    setPpeFundingFee(PROGRAM_FIELD_DEFAULTS.vaFundingFee);
    setPpeVaSubseq(false);
    setPpeVaExempt(false);
    setPpeGuaranteeFee(PROGRAM_FIELD_DEFAULTS.usdaGuaranteeFee);

    // reset error
    setPropteryError('');
    setFicoError('');
    setTargetPriceMessage('');
  };

  // Income documentation: a choice for Non-QM and second liens; Conventional,
  // FHA, VA and USDA always price full documentation (plan D9, D24), shown as
  // text, not as a box that looks editable (audit form item 11).
  const field_IncomeDocumentation = () => {
    if (!INCOME_DOCUMENTATION_PROGRAMS.includes(loanProgram)) {
      return (
        <Field
          id="ppe-income-documentation"
          title="Income documentation"
          tip="Conventional, FHA, VA and USDA are priced on full documentation. Pick Non-QM or a second lien to choose another type."
          tipLabel="About income documentation"
          className="ppe-income-documentation-field"
        >
          <ReadOnlyValue id="ppe-income-documentation">Full Doc</ReadOnlyValue>
        </Field>
      );
    }
    const secondLien = isSecondLienProgram(loanProgram);
    const documentation = secondLien ? secondIncomeDoc : nqIncomeDoc;
    const setDocumentation = secondLien ? setSecondIncomeDoc : setNqIncomeDoc;
    const dscrMode = secondLien ? secondDscrMode : nqDscrMode;
    const setDscrMode = secondLien ? setSecondDscrMode : setNqDscrMode;
    const dscr = secondLien ? secondDscr : nqDscr;
    const setDscr = secondLien ? setSecondDscr : setNqDscr;
    return (
      <>
        <Field id="ppe-income-documentation" title="*Income documentation" className="ppe-income-documentation-field">
          <Select
            id="ppe-income-documentation"
            className="ppe-input"
            // The Product column is narrow; let the list be as wide as its
            // longest name ("Borrower-prepared P&L (months not stated)")
            // instead of cutting it.
            popupMatchSelectWidth={false}
            // The whole name shows in the box, so no browser tooltip: antd puts
            // the option name in a title attribute, and the tooltip it drew
            // ("CPA P&L") covered the Target price label below.
            title=""
            value={documentation}
            onChange={(value) => {
              setDocumentation(value);
              if (value === DSCR_DOCUMENTATION) setDscrMode("number");
            }}
            // Grouped Full Doc / Bank statements / P&L / 1099 / WVOE / Assets /
            // DSCR; the closed box shows the whole name, group included
            // where the option's own name does not say it.
            options={incomeDocumentationOptions({ aaaConnected, current: documentation })}
            labelRender={({ value }) => incomeDocumentationName(value)}
          />
        </Field>
        {documentation === DSCR_DOCUMENTATION && (
          <Field id="ppe-dscr" title="*DSCR" className="ppe-dscr-field">
            <div className={`ppe-dscr-choice ${dscrMode === "noRatio" ? "is-no-ratio" : "is-number"}`}>
              <InputNumber
                id="ppe-dscr"
                className="ppe-input ppe-dscr-value"
                placeholder="Enter DSCR"
                value={dscrMode === "number" ? dscr : null}
                disabled={dscrMode === "noRatio"}
                onFocus={() => setDscrMode("number")}
                onChange={(value) => {
                  setDscrMode("number");
                  setDscr(value ?? 1.2);
                }}
                min={0.01}
                step={0.05}
              />
              <ToggleButton
                className="ppe-dscr-no-ratio"
                on={dscrMode === "noRatio"}
                onClick={() => setDscrMode(dscrMode === "noRatio" ? "number" : "noRatio")}
              >
                No Ratio
              </ToggleButton>
            </div>
          </Field>
        )}
      </>
    );
  };

  const field_Product = () => {
    const nonQm = loanProgram === "nonqm";
    const agency = AGENCY_PROGRAMS.includes(loanProgram);
    const owner = checkAccess([UR_Owner]);
    const byRate = searchBy === "rate";
    const morePricingSet = [
      nonQm && Number(ppePrepay) > 0,
      agency && ausType.length > 0,
    ].filter(Boolean).length;
    return (
      <div className="search-fields-content-item product_field">
        <div className="search-fields-content-item-title">
          <div className="search-fields-content-item-title-svg">
            {product_svg()}
          </div>
          <span className="search-fields-content-item-title-text">Product</span>
        </div>
        <div className="search-fields-content-item-content">
          {field_IncomeDocumentation()}
          <Field
            id={byRate ? "ppe-target-rate" : "ppe-target-price"}
            title={byRate ? "Target rate" : "Target price (base 100)"}
            tip={byRate ? undefined : targetPriceTip(targetBasis)}
            tipLabel="About target price"
            error={targetPriceMessage}
            end={byRate ? undefined : <TargetBasisPicker id="ppe-target-basis" value={targetBasis} onChange={setTargetBasis} />}
          >
            <div className="ppe-target-row">
              <Segmented
                className="ppe-seg ppe-target-by"
                aria-label="Target by"
                value={searchBy}
                onChange={updateSearchBy}
                options={[
                  { label: "Price", value: "price" },
                  { label: "Rate", value: "rate" },
                ]}
              />
              {byRate ? (
                <InputNumber
                  key="rate"
                  id="ppe-target-rate"
                  className="ppe-input ppe-target-value"
                  step={0.125}
                  precision={3}
                  min={0}
                  max={20}
                  suffix="%"
                  placeholder="6.500"
                  value={targetRate}
                  status={targetPriceMessage ? "error" : ""}
                  aria-describedby={targetPriceMessage ? "ppe-target-rate-error" : undefined}
                  onChange={updateTargetRate}
                />
              ) : (
                <InputNumber
                  key="price"
                  id="ppe-target-price"
                  className="ppe-input ppe-target-value"
                  step={0.125}
                  placeholder="Enter target price"
                  value={targetPrice}
                  status={targetPriceMessage ? "error" : ""}
                  aria-describedby={targetPriceMessage ? "ppe-target-price-error" : (targetBasis === TARGET_BASIS_COST ? "ppe-target-price-hint" : undefined)}
                  onChange={updateTargetPrice}
                />
              )}
            </div>
            {!byRate && (
              <TargetBasisHint
                id="ppe-target-price-hint"
                basis={targetBasis}
                price={targetPrice}
                loanAmount={formSubjectLoanAmount()}
              />
            )}
          </Field>
          <Field id="ppe-lock-days" title="Lock days" group>
            <Choice
              id="ppe-lock-days"
              value={lockDay}
              onChange={updateLockDay}
              options={[12, 15, 30, 45, 60, 75].map((days) => ({ value: days, label: String(days), title: `${days} days` }))}
            />
          </Field>
          <Field
            id="ppe-compensation"
            title="Compensation"
            group
            tip={compensationType === "lender_paid"
              ? (owner ? "The lender-paid percentage is set in Lender settings." : "The lender-paid percentage is set by the account administrator.")
              : "Borrower-paid compensation, as a percent of the loan amount."}
            tipLabel="About compensation"
            end={compensationType === "lender_paid" && owner && (
              <Button type="link" className="ppe-inline-link" onClick={handleEditCompensation}>Lender settings</Button>
            )}
          >
            <div className="ppe-compensation-row">
              <Choice
                id="ppe-compensation"
                value={compensationType}
                onChange={updateCompensationType}
                // "Lender" / "Borrower" (who pays it; the full name is the
                // title): "Borrower paid" was cut once the percent box took a
                // third of the row.
                options={[
                  { value: "lender_paid", label: "Lender", title: "Lender paid" },
                  { value: "borrower_paid", label: "Borrower", title: "Borrower paid" },
                ]}
              />
              {compensationType !== "lender_paid" && (
                <InputNumber
                  aria-label="Compensation percent"
                  className="ppe-input ppe-compensation-percent"
                  placeholder="0"
                  suffix="%"
                  value={compensationPercent}
                  onChange={updateCompensationPercent}
                />
              )}
            </div>
          </Field>
          <Field
            id="ppe-estimated-closing-fee"
            title="Estimated closing fee"
            tip={owner ? "Set in Edit fees; the results show each rate's closing costs." : "Closing fee is set by the account administrator."}
            tipLabel="About the estimated closing fee"
            end={owner && (
              <Button type="link" className="ppe-inline-link ppe-edit-fees" onClick={handleEditCostDetails}>Edit fees</Button>
            )}
          >
            <ReadOnlyValue id="ppe-estimated-closing-fee" muted={isCalculatedAtResults}>
              {isCalculatedAtResults
                ? "Fees shown on results page"
                : `$${moneyFormatter(Number(estimatedClosingFee) || 0)}`}
            </ReadOnlyValue>
          </Field>
          <div className="search-fields-content-item-content-item ppe-checks">
            <Checkbox checked={ppeInterestOnly} onChange={(e) => setPpeInterestOnly(e.target.checked)}>Interest only</Checkbox>
            <Checkbox checked={isImpound} onChange={updateImpound}>Impound</Checkbox>
          </div>
          {/* Prepay Penalty only for Non-QM, AUS type only for Conventional,
              FHA, VA and USDA; Buydown is gone (plan D28). */}
          {(nonQm || agency) && (
            <Fold id="more-pricing" title="More pricing options" setCount={morePricingSet}>
              {nonQm && (
                <Field id="ppe-prepay" title="Prepay penalty (years)" group>
                  <Choice
                    id="ppe-prepay"
                    value={Number(ppePrepay)}
                    onChange={setPpePrepay}
                    options={[0, 1, 2, 3, 4, 5].map((years) => ({ value: years, label: years === 0 ? "None" : String(years) }))}
                  />
                </Field>
              )}
              {agency && (
                <Field id="ppe-aus-type" title="AUS type">
                  <Select
                    id="ppe-aus-type"
                    className="ppe-input"
                    placeholder="Any"
                    mode="multiple"
                    maxTagCount="responsive"
                    showSearch={false}
                    allowClear
                    value={ausType}
                    onChange={setAusType}
                    options={[
                      { value: "DU", label: "DU" },
                      { value: "LP", label: "LP" },
                      { value: "Manual", label: "Manual UW" },
                    ]}
                  />
                </Field>
              )}
            </Fold>
          )}
        </div>
      </div>
    );
  };
  const getLender = () => {
    setIsLoading(true);
    const token = sessionStorage.getItem('ZeitroA')
    fetch('/data/getcustomerlenders', {
      method: 'GET',
      headers: {
          Authorization: 'Bearer ' + token,
          Cache: 'no-cache',
      },
  })
      .then((response) => {
          if (response.status !== 200) {
              console.warn(
                  'Looks like there was a problem. Status Code: ' +
                      response.status
              )
              return
          }
          // Examine the text in the response
          response.json().then((js) => {
              if (js.Status === 'OK') {
                  setAaaConnected(isAaaConnected(js.Lenders))
                  if (js.Lenders !== null) {
                      const hasLenders =
                          js.Lenders.some((item) => item.Active) ||
                          js.Lenders.some((item) => item.Hidden)
                      if (hasLenders) {
                          setHasLenders(true)
                      }
                  }
              }
          }).finally(() => {
            setIsLoading(false)
          })
      })
      .catch(function (err) {
          console.error('Fetch lenders Error : ', err)
      })
  }

  const check_property_value = (type, value) => {
    let propertyValue_ = propertyValue;
    let loanAmount_ = loanProgram === "heloc" ? ppeLine : loanAmount;
    let downPayment_ = downPayment;
    if(type === 'propertyValue'){
     propertyValue_ = value
    }
    if(type === 'loanAmount'){
      loanAmount_ = value
    }
    if(type === 'downPayment'){
      downPayment_ = value
    }
    if (!(Number(propertyValue_) > 0)) {
      setPropteryError('Home value must be greater than $0.');
      return false;
    }
    if (loanPurpose === 'Purchase' && !isSecondLienProgram(loanProgram)) {
      if(propertyValue_ < downPayment_){
        // message.error('Property value must be greater than down payment.');
        setPropteryError('Property value must be greater than down payment.');
        return false;
      }else{
        setPropteryError('');
      }
    }else{
      if(propertyValue_ < loanAmount_){
        // message.error('Property value must be greater than loan amount.');
        setPropteryError('Property value must be greater than loan amount.');
        return false;
      }else{
        setPropteryError('');
      }
    }
  }

  const check_rates = () => {
    const derivedRatios = getDerivedLoanMetrics();
    const validation = validatePpeSearch({
      loanPurpose,
      loanProgram,
      propertyValue,
      appraisedValue: ppeAppraised,
      appraisalActive: ppeAdvOpen && ppeAppraisalOverrideActive,
      downPayment,
      loanAmount: loanProgram === "heloc" ? ppeDraw : getDerivedFirstLienAmounts().baseLoanAmount,
      lineAmount: ppeLine,
      drawAmount: ppeDraw,
      drawTermMonths: ppeDrawTermMonths,
      primaryMortgageAmount: ppePrimaryMtg,
      cashOutAmount: ppeCashOut,
      secondaryLoanAmount,
      advancedOpen: ppeAdvOpen,
      totalLoanAmount: ppeAdvOpen ? getDerivedFirstLienAmounts().totalLoanAmount : undefined,
      cltvOverride: ppeCltv,
      hcltvOverride: ppeHcltv,
      ltv: derivedRatios.ltv,
      cltv: getEffectiveRatios().cltv,
      hcltv: getEffectiveRatios().hcltv,
      fico,
      noFico: PPE_FEATURES.noFico && noFico,
      loanTerms: selectedLoanTerms,
      units: ppeUnits,
      propertyType,
      state,
      county,
      zip: ppeZip,
      zipCounties: currentZipCounties(),
      monthlyIncome: isDscrSelected() ? null : monthlyIncome,
      monthlyDebt: isDscrSelected() ? null : monthlyDebt,
      reserves: ppeReserves,
      propertiesFinanced: ppePropsFinanced,
      lockDays: lockDay,
      searchBy,
      targetPrice,
      targetRate,
      compensationType,
      compensationPercent,
      fundingFee: ppeFundingFee,
      vaExempt: ppeVaExempt,
      guaranteeFee: ppeGuaranteeFee,
      requiresDscr: INCOME_DOCUMENTATION_PROGRAMS.includes(loanProgram)
        && (isSecondLienProgram(loanProgram) ? secondIncomeDoc : nqIncomeDoc) === "DebtServiceCoverageRatio",
      dscrMode: isSecondLienProgram(loanProgram) ? secondDscrMode : nqDscrMode,
      dscr: isSecondLienProgram(loanProgram) ? secondDscr : nqDscr,
      lateCounts: Object.values(nqLates),
    });
    if (!validation.valid) {
      const first = validation.firstError;
      if (first.field === "targetPrice" || first.field === "targetRate") setTargetPriceMessage(first.message);
      if (first.field === "fico") setFicoError(first.message);
      if (["cashOutAmount"].includes(first.field)) setPpeCashOutError(first.message);
      if (["lineAmount", "drawAmount"].includes(first.field)) setPpeHelocAmountError(first.message);
      if (["propertyValue", "downPayment"].includes(first.field)) setPropteryError(first.message);
      // A field with a place for its error shows it there, once, and takes
      // the focus; the others say it in a message (audit form item 16).
      const inline = INLINE_ERROR_FIELDS[first.field];
      if (inline && document.getElementById(inline)) {
        document.getElementById(inline).focus();
      } else {
        message.error(first.message);
      }
      return;
    }
    setTargetPriceMessage("");
    setFicoError("");

    if (loanProgram === "va" && !ppeVaExempt && !(Number(ppeFundingFee) > 0)) {
      message.error("Funding Fee must be greater than 0 unless VA Exempt Funding Fee is selected.");
      return;
    }

    if (loanProgram === "heloc" && !validatePpeHelocAmounts(ppeLine, ppeDraw)) {
      message.error("Draw amount cannot exceed the line amount.");
      return;
    }

    if(!hasLenders){
      setIsOpen_no_lender(true);
      return;
    }
    if(check_property_value() === false){return};
    // 获取用户信息
    const savedUserInfo = JSON.parse(
      sessionStorage.getItem("statsig_user_info")
    );
    const customerid = com.getCustomerId();
    const secondLienProgram = isSecondLienProgram(loanProgram);
    const firstLienAmounts = getDerivedFirstLienAmounts();
    const subjectLoanAmount = loanProgram === "heloc"
      ? Number(ppeDraw) || 0
      : Number(loanAmount) || 0;
    // Income and debt only count with an income (plan D3), and never for
    // DSCR, which prices on the property's rent (D23).
    const hasIncome = !isDscrSelected() && Number(monthlyIncome) > 0;

    // 构建请求体
    const body = {

      Rate_CRAAdjustment: false, // flag
      Rate_LMIBAdjustment: false, // flag
      Rate_LMITAdjustment: false, // flag
      Rate_MMCTAdjustment: false, // flag
      appraisalWaiver: false, // flag
      borrowerPaidCompensationAmount: 0, // flag
      currentJobInMonths: 24, // flag
      familyRelatedOrSmallBusiness: false, // flag
      getFiles: false, // flag
      giftFundUsed: false, // flag
      // monthlyDebt: 1000, // flag
      // monthlyIncome: 10000, // flag
      pmi: 0.51, // flag
      point: [{lender: "default", percent: 1, fixed: 500}], // flag
      rsuAsIncome: -1, // flag
      unit: 1, // flag
      visaType: "H_1B", // flag

      // loan
      loanProgram: loanProgram, // PPE: explicit merged program selector
      // PPE: additional unified-pricing fields.
      lienPosition: ppeLien,
      amortizationType: isArmTerm(loanTerm) ? "arm" : "fixed",
      armFixedTerm: ARM_TERM_YEARS[loanTerm],
      appraisedValue: getEffectivePpeAppraisedValue(),
      totalLoanAmount: secondLienProgram ? undefined : firstLienAmounts.totalLoanAmount,
      // A cash-out HELOC's cash is its draw (plan D35); pricingRequest.js
      // sends it.
      cashOutAmount: loanPurpose === "CashOutRefi" && loanProgram !== "heloc" ? ppeCashOut : undefined,
      cltvInput: ppeCltv,
      hcltvInput: ppeHcltv,
      lineAmount: loanProgram === "heloc" ? ppeLine : undefined,
      drawAmount: loanProgram === "heloc" ? ppeDraw : undefined,
      drawTermMonths: loanProgram === "heloc" ? ppeDrawTermMonths : undefined,
      primaryMortgageAmount: secondLienProgram ? ppePrimaryMtg : undefined,
      fundingFee: ppeVaExempt ? undefined : ppeFundingFee,
      vaSubsequentUse: ppeVaSubseq,
      vaExemptFundingFee: ppeVaExempt,
      guaranteeFee: ppeGuaranteeFee,
      reserves: ppeReserves,
      propertiesFinanced: ppePropsFinanced,
      numberOfUnits: ppeUnits,
      propertyZip: ppeZip,
      prepaymentPenalty: ppePrepay,
      interestOnlyNew: ppeInterestOnly,
      purpose: loanPurpose,
      loanTerm: loanTerm,
      selectedLoanTerms: selectedLoanTerms,
      loanTypes: loanType,
      propertyValue: propertyValue, // new
      downPayment: !secondLienProgram && loanPurpose === "Purchase" ? downPayment : undefined,
      // Purchase collects downPayment in the UI, so derive the loan amount at
      // request time (mirrors the legacy flow) instead of trusting the state.
      loanAmount: secondLienProgram
        ? subjectLoanAmount
        : firstLienAmounts.baseLoanAmount,
      upFrontMIP: upFrontMIP,
      upFrontMIP_Financed: upFrontMIP_Financed,
      LTV: derivedRatios.ltv,
      // Derived from the subordinate financing actually entered (see
      // getDerivedRatios); the ppeCltv/ppeHcltv inputs override when filled in.
      CLTV: getEffectiveRatios().cltv,
      HCLTV: getEffectiveRatios().hcltv,
      secondaryLoanAmount: secondLienProgram ? undefined : secondaryLoanAmount, // new
      // isSubordinateLiens: isSubordinateLiens, // new

      // property
      occupancy: com.getOccupancy(occupancy),
      property: com.getPropertyType(propertyType === NON_WARRANTABLE_CONDO_PROPERTY_TYPE ? "condominium" : propertyType),
      nonWarrantableCondo: propertyType === NON_WARRANTABLE_CONDO_PROPERTY_TYPE ? true : undefined,
      state: state,
      county: county,

      // borrower
      FICO: PPE_FEATURES.noFico && noFico ? null : fico,
      noFico: PPE_FEATURES.noFico && noFico ? true : undefined,
      monthlyIncome: hasIncome ? parseFloat(monthlyIncome) : null,
      monthlyDebt: hasIncome && monthlyDebt !== null && monthlyDebt !== undefined && monthlyDebt !== "" ? parseFloat(monthlyDebt) : null,
      dti: hasIncome ? dti : null,
      citizenship: citizenship,
      vesting: PPE_FEATURES.entityVesting ? vesting : undefined,
      firstTimeHomeBuyer: isFirstTimeHomeBuyer,
      selfEmployed: isSelfEmployed,

      // product
      // By price or by rate (11-04); the results area reads searchBy to
      // decide what the target means.
      searchBy: searchBy,
      targetPrice: targetPrice,
      targetRate: searchBy === "rate" ? targetRate : undefined,
      // Price or Cost to borrower (11-04), kept by a search by rate too so
      // the saved search comes back with it.
      targetPriceBasis: targetBasis,
      lockDay: lockDay,
      compensationType: compensationType, // new
      compensationPercent: compensationPercent, // new
      estimatedClosingFee: parseFloat(estimatedClosingFee), 
      ausTypes: ausType, // new

      isImpound: isImpound, // new
      escrowWaiver: !isImpound, // new

      isInterestOnly: isInterestOnly, // new
      isWaiveLenderFee: isWaiveLenderFee, // new
    };

    // PPE: Non-QM keeps its full details block; both second-lien programs reuse
    // its income documentation/DSCR inputs because they use the Non-QM engine.
    if (INCOME_DOCUMENTATION_PROGRAMS.includes(loanProgram)) {
      const incomeDocumentation = isSecondLienProgram(loanProgram) ? secondIncomeDoc : nqIncomeDoc;
      const dscrMode = isSecondLienProgram(loanProgram) ? secondDscrMode : nqDscrMode;
      const dscr = isSecondLienProgram(loanProgram) ? secondDscr : nqDscr;
      const nonQm = {
        incomeDocumentation: [incomeDocumentation],
      };
      if (incomeDocumentation === "DebtServiceCoverageRatio") {
        nonQm.dscrMode = dscrMode;
        if (dscrMode === "number") nonQm.dscr = Number(dscr);
      }
      if (loanProgram === "nonqm") {
        if (nqHasMortgageLates) {
          nonQm.mortgageLates = {
            last12mo: { x30: nqLates.l12_30, x60: nqLates.l12_60, x90: nqLates.l12_90, x120: nqLates.l12_120 },
            m13to24: { x30: nqLates.l24_30, x60: nqLates.l24_60, x90: nqLates.l24_90, x120: nqLates.l24_120 },
          };
        }
        if (nqHousingEvent) {
          nonQm.housingEventType = nqHousingEvent;
          if (nqHousingSeasoning) nonQm.housingEventSeasoning = nqHousingSeasoning;
        }
        if (nqBkType) {
          nonQm.bankruptcyType = nqBkType;
          if (nqBkOutcome) nonQm.bankruptcyOutcome = nqBkOutcome;
          if (nqBkSeasoning) nonQm.bankruptcySeasoning = nqBkSeasoning;
        }
        if (nqShortTermRental) nonQm.shortTermRental = true;
      }
      body.nonQm = nonQm;
    }

    // 调用父组件的回调函数
    onCheckRates(body, savedUserInfo, customerid);
  };

  const handleEditCostDetails = () => {
    setShowEditCostDetails(true);
  };

  const handleEditCostDetailsModal = () => {
    setShowEditCostDetails(false);
  };
  let setEstClosingfee = (lodeStarConfig, closingFeeConfig) => {

    let data = {
        lodeStarConfig: {
            ...lodeStarConfig,
            isModified: true,
            enableCanNotShopServices: false,
            enablePrepaids: false,
            enableInitialEscrowPaymentsAtClosing: false
        },
        closingCostsDetail: closingFeeConfig
    }
    let token = sessionStorage.getItem("ZeitroA");
    fetch("/los/setloanestimatedclosingfee", {
        cache: "no-cache",
        method: "POST",
        headers: {
            Authorization: "Bearer " + token,
            "Content-Type": "application/json",
            Cache: "no-cache",
        },
        body: JSON.stringify(data)
    }).then(response => {
        if (!response.ok) {
        } else {
            setEstimatedClosingFee(totalClosingFee)
            getloanestimatedclosingfee()
        }
    }).catch(error => {
    })
  }

  const handleLodestarChange = (checked) => {
    let newLodeStarConfig = {
      ...lodeStarConfig,
      enableCanNotShopServices: checked,
      enableCanShopServices: checked,
      enableTaxesAndOtherGovernmentFees: checked,
      enablePrepaids: checked,
      enableInitialEscrowPaymentsAtClosing: checked,
      enableOther: checked,
    }
    const closingFee = closingCostRef.current.getClosingFee()
    setEstClosingfee(newLodeStarConfig, closingFee)
  };

  const handleConfigChange = (newConfig) => {
    const closingFee = closingCostRef.current.getClosingFee()
    setEstClosingfee(newConfig, closingFee)
  };

  const handleSaveClosingFee = () => {
    setEstimatedClosingFee(totalClosingFee);
    const closingFee = closingCostRef.current.getClosingFee();
    setEstClosingfee(lodeStarConfig, closingFee)
    handleEditCostDetailsModal();
  };

  const getEditCostsModal = () => {
    return (
      <Modal
        className="edit-fees-modal"
        centered
        destroyOnClose
        maskClosable={true}
        width={974}
        open={showEditCostDetails}
        title=""
        onCancel={handleEditCostDetailsModal}
        footer={null}
      >
        <div className="edit-fees-modal-title">Closing cost details</div>
        <div className="edit-fees-modal-content edit-fees-modal-content-new">
          <div style={{ marginBottom: 12 }}>
            <LodestarTipsCard
              initialConfig={lodeStarConfig}
              onLodestarChange={handleLodestarChange}
            />
          </div>
          <div className="estimatedclosingfee-container">
            <ClosingCost
              ref={closingCostRef}
              initialConfig={lodeStarConfig}
              initialClosingFee={closingFeeConfig}
              onConfigChange={handleConfigChange}
              onTotalFeesChange={(totalFees) => setTotalClosingFee(totalFees)}
            />
          </div>
          <div className="edit-fees-modal-footer">
            <Button
              className="edit-fees-modal-cancel"
              onClick={handleEditCostDetailsModal}
              variant="customize"
            >
              Cancel
            </Button>
            <Button
              className="edit-fees-modal-save"
              variant="primary"
              onClick={handleSaveClosingFee}
            >
              Save
            </Button>
          </div>
        </div>
      </Modal>
    );
  };

  const [addLenderModal, setAddLenderModal] = useState(false);

  const handleCloseAddLenderModal = () => {
    setAddLenderModal(false);
  };

  const AddLenderModal = () => {
    return (
      <Modal
        open={addLenderModal}
        onCancel={handleCloseAddLenderModal}
        centered
        footer={[
          <Button key="back" onClick={handleCloseAddLenderModal}>
            Cancel
          </Button>,
        ]}
        width={1200}
      >
        <Lenders addLenders={addLenders} statsigSource="rates"/>
      </Modal>
    );
  };
  const svg_collapse = () => {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M17.8107 10.5607L16.75 9.5L12.1544 14.0956L7.56066 9.50184L6.5 10.5625L11.0937 15.1562L11.0931 15.1569L12.1538 16.2175L12.1544 16.2169L12.1569 16.2194L13.2175 15.1587L13.2151 15.1563L17.8107 10.5607Z" fill="#222222"/>
      </svg>
    )
  }

  const backToResult = () => {
    onBackToResult();
  }

  const addLenders = (has) => {
    setHasLenders(has);
    setIsOpen_no_lender(false);
    closeModal_no_lender();
    
    if (has) {
      setShouldCheckRatesAfterLenderUpdate(true);
    }
  }
  // (removed: legacy loanType->UFMIP dollar recompute; upFrontMIP is now a
  // percent and only sent for loanProgram=fha at request-build time)

  useEffect(() => {
    if (shouldCheckRatesAfterLenderUpdate && hasLenders) {
      setShouldCheckRatesAfterLenderUpdate(false);
      check_rates();
    }
  }, [hasLenders, shouldCheckRatesAfterLenderUpdate]);

  const seasoningOptions = [
    { value: "lt12", label: "0-11 months" },
    { value: "12-23", label: "12-23 months" },
    { value: "24-35", label: "24-35 months" },
    { value: "36-47", label: "36-47 months" },
    { value: "48-59", label: "48-59 months" },
    { value: "60-83", label: "60-83 months" },
    { value: "gte84", label: "84+ months" },
  ];

  // Non-QM credit & investor, folded in the Borrower column (plan section
  // 6.0): housing event, bankruptcy, mortgage lates, and vesting.
  const field_NonQMCredit = () => (
    <div className="ppe-nonqm-credit">
      {/* Six events: a list, not buttons that would not fit the column. */}
      <Field id="ppe-housing-event" title="Housing event">
        <Select
          id="ppe-housing-event"
          className="ppe-input"
          value={nqHousingEvent}
          onChange={(value) => {
            setNqHousingEvent(value);
            setNqHousingSeasoning(value ? DEFAULT_SEASONING_BUCKET : "");
          }}
          options={[
            { value: "", label: "None" },
            { value: "foreclosure", label: "Foreclosure" },
            { value: "shortSale", label: "Short sale" },
            { value: "deedInLieu", label: "Deed-in-lieu" },
            { value: "forbearance", label: "Forbearance" },
            { value: "modification", label: "Modification" },
          ]}
        />
      </Field>
      {nqHousingEvent && (
        <Field id="ppe-housing-seasoning" title="Housing event seasoning">
          <Select id="ppe-housing-seasoning" className="ppe-input" value={nqHousingSeasoning} onChange={setNqHousingSeasoning} options={seasoningOptions} />
        </Field>
      )}
      {/* Lists, not buttons: the buttons were cut to "Chapt…" and
          "Discharg…" in a narrow column (user 11-04). */}
      <Field id="ppe-bankruptcy-type" title="Bankruptcy">
        <Select
          id="ppe-bankruptcy-type"
          className="ppe-input"
          value={nqBkType}
          onChange={(value) => {
            setNqBkType(value);
            setNqBkOutcome(value ? DEFAULT_BANKRUPTCY_OUTCOME : "");
            setNqBkSeasoning(value ? DEFAULT_SEASONING_BUCKET : "");
          }}
          options={[
            { value: "", label: "None" },
            { value: "chapter7", label: "Chapter 7" },
            { value: "chapter11", label: "Chapter 11" },
            { value: "chapter13", label: "Chapter 13" },
          ]}
        />
      </Field>
      {nqBkType && (
        <>
          <Field id="ppe-bankruptcy-outcome" title="Bankruptcy outcome">
            <Select
              id="ppe-bankruptcy-outcome"
              className="ppe-input"
              value={nqBkOutcome}
              onChange={setNqBkOutcome}
              options={[
                { value: "discharged", label: "Discharged" },
                { value: "dismissed", label: "Dismissed" },
                { value: "openPending", label: "Open / pending" },
              ]}
            />
          </Field>
          <Field id="ppe-bankruptcy-seasoning" title="Bankruptcy seasoning">
            <Select id="ppe-bankruptcy-seasoning" className="ppe-input" value={nqBkSeasoning} onChange={setNqBkSeasoning} options={seasoningOptions} />
          </Field>
        </>
      )}
      <Field id="ppe-mortgage-lates" title="Any mortgage late payments?" group>
        <Choice
          id="ppe-mortgage-lates"
          className="ppe-mortgage-lates-choice"
          value={nqHasMortgageLates ? "yes" : "no"}
          onChange={(value) => setNqHasMortgageLates(value === "yes")}
          options={[{ value: "no", label: "No" }, { value: "yes", label: "Yes" }]}
        />
      </Field>
      {nqHasMortgageLates && (
        <div className="ppe-lates" role="group" aria-label="Mortgage late counts">
          <div className="ppe-lates-row">
            <div className="ppe-lates-rowlabel" />
            {["×30", "×60", "×90", "×120"].map((h) => (
              <div className="ppe-lates-colhead" key={h}>{h}</div>
            ))}
          </div>
          <div className="ppe-lates-row">
            <div className="ppe-lates-rowlabel">Last 12 months</div>
            {["l12_30", "l12_60", "l12_90", "l12_120"].map((k) => (
              <InputNumber key={k} aria-label={`Last 12 months, ${k.split("_")[1]} days late`} className="ppe-lates-input" controls={false} min={0} value={nqLates[k]} onChange={setLate(k)} />
            ))}
          </div>
          <div className="ppe-lates-row">
            <div className="ppe-lates-rowlabel">13-24 months</div>
            {["l24_30", "l24_60", "l24_90", "l24_120"].map((k) => (
              <InputNumber key={k} aria-label={`13 to 24 months, ${k.split("_")[1]} days late`} className="ppe-lates-input" controls={false} min={0} value={nqLates[k]} onChange={setLate(k)} />
            ))}
          </div>
        </div>
      )}
      {/* Entity Vesting (plan D12): lenders price it only in the programs
          their guidelines allow. First Time Investor is not offered: the
          production calculator answers 400 for it (D15). */}
      {PPE_FEATURES.entityVesting && (
        <div className="search-fields-content-item-content-item ppe-checks">
          <Checkbox
            checked={vesting === "entity"}
            onChange={(e) => setVesting(e.target.checked ? "entity" : "individual")}
          >
            Entity vesting (LLC)
          </Checkbox>
        </div>
      )}
    </div>
  );
  return (
    <div className="search-fields">
      <div className="title-box">
        <div className="title-box-left" style={{display: "flex", alignItems: "center"}}>
          Search fields
          {from === "result" && (
            <Button type="text" className="ppe-back-to-results" aria-label="Back to results" onClick={() => backToResult()}>
              {svg_collapse()}
            </Button>
          )}
        </div>
        <div className="title-box-right">
          <Button type="text" className="title-box-right-reset" onClick={reset_all}>
            Reset all
          </Button>
          {!isLoading &&
          <Button type="primary" className="title-box-right-check" onClick={check_rates}>
            Check rates
          </Button>}
        </div>
      </div>
      {/* One control size for the whole form (Option B): 32px controls,
          6px corners, 14px text (audit form items 5 and 8). */}
      <ConfigProvider theme={FORM_THEME}>
        <div className="search-fields-content">
          {field_Loan()}
          {field_Property()}
          {field_Borrower()}
          {field_Product()}
        </div>
      </ConfigProvider>

      {/* no lender existing */}
      <NoLenderModal isOpen={isOpen_no_lender} onClose={closeModal_no_lender} addLenders={addLenders}/>
      {getEditCostsModal()}
      <AddLenderModal />
    </div>
  );
};

export default SearchFields;
